import { componentTranslations } from '@/i18n/components';
import { paymentTranslations } from '@/utils/translations/paymentTranslations';

type SupportedLanguage = 'en' | 'fr' | 'es' | 'sq' | 'ru' | 'pt' | 'el' | 'de' | 'ro' | 'sr' | 'pl';

export const SUPPORTED_LANGUAGES = {
  en: 'English',
  fr: 'Français', 
  es: 'Español',
  sq: 'Shqip',
  ru: 'Русский',
  pt: 'Português',
  el: 'Ελληνικά',
  de: 'Deutsch',
  ro: 'Română',
  sr: 'Српски',
  pl: 'Polski',
} as const;

const translations = {
  en: {
    'common.back': 'Back',
    'common.next': 'Next',
    'common.save': 'Save',
    'common.submit': 'Submit',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.remove': 'Remove',
    'common.confirm': 'Confirm',
    'common.close': 'Close',
    'common.clear': 'Clear',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.note': 'Note',
    'common.optional': 'Optional',
    'common.required': 'Required',
    'common.message': 'Message',
    'common.recipient': 'Recipient',
    'common.id': 'ID',
    'common.amount': 'Amount',
    'common.reference': 'Reference',
    'common.goBack': 'Go Back',
    'common.transactionId': 'Transaction ID',
    'common.currency': 'Currency',
    'common.loadingProfile': 'Loading profile...',
    'common.give_money_to': 'Give money to {{name}}',
    'common.give_money_to_stranger': 'Give money to this stranger',
    'common.kybVerified': 'KYB Verified',
    'common.kybPending': 'KYB Pending',
    'common.kybRejected': 'KYB Rejected',
    'common.notListed': 'Not Listed',
    'common.unknown': 'Unknown',
    'userPublicProfile.loading': 'Loading profile...',
    'userPublicProfile.notFound': 'Profile Not Found',
    'userPublicProfile.notFoundDesc': 'User profile not found',
    'userPublicProfile.memberSince': 'Member since',
    'userPublicProfile.sendMoney': 'Send Money',
    'userPublicProfile.profileInformation': 'Profile Information',
    'userPublicProfile.userId': 'User ID',
    'userPublicProfile.username': 'Username',
    'userPublicProfile.notSet': 'Not set',
    'userPublicProfile.roles': 'Roles',
    'guest.continue_as_guest': 'Continue as Guest',
    'guest.login_description': 'Full access for 48 hours. Sign-up to be able to login',
    'guest.upgrade_account': 'Upgrade Account',
    'guest.upgrade_prompt.title': 'Upgrade Your Guest Account',
    'guest.upgrade_prompt.description': 'Save your history and unlock all features by registering.',
    'guest.upgrade_prompt.continue': 'Continue to Upgrade',
    'guest.session_indicator.title': 'Guest Session',
    'guest.session_indicator.time_left': 'Time Left',
    'guest.session_indicator.sign_up_notice': 'Sign up to save your data and continue using the app.',
    'guest.session_indicator.session_expired': 'Session Expired',
    'guest.session_indicator.session_expired_message': 'Your guest session has expired. Please sign up or log in to continue.',
    'guest.session_indicator.sign_up': 'Sign Up',
    'guest.logout.warning.title': 'Sign Out as Guest?',
    'guest.logout.warning.description': 'If you sign out, you will lose access to your guest account. Any cards you\'ve added and your transaction history will become inaccessible unless you sign up for a permanent account.',
    'guest.logout.cancel': 'Cancel',
    'guest.logout.confirm': 'Sign Out Anyway',
    'guest.learn_more_join': 'Learn more and join',
    'vaiu.sign_in': 'Sign In',
    'vaiu.sign_up': 'Sign Up',
    'vaiu.or': 'or',
    'vaiu.password': 'Password',
    'vaiu.forgot_password': 'Forgot password?',
    'vaiu.signing_in': 'Signing In...',
    'vaiu.otp_login.title': 'Sign in with Phone',
    'vaiu.otp_login.description': 'Enter your phone number to receive a one-time code.',
    'vaiu.otp_login.phone_placeholder': 'Your phone number',
    'vaiu.otp_login.request_code': 'Request Code',
    'vaiu.otp_login.requesting_code': 'Requesting Code...',
    'vaiu.otp_login.enter_code_title': 'Enter Verification Code',
    'vaiu.otp_login.enter_code_description': 'A code has been sent to {{phoneNumber}}',
    'vaiu.otp_login.verify': 'Verify',
    'vaiu.otp_login.verifying': 'Verifying...',
    'vaiu.otp_login.resend_code': 'Resend Code',
    'vaiu.otp_login.resend_code_in': 'Resend code in {{seconds}}s',
    'vaiu.otp_login.wrong_number': 'Wrong number?',
    'vaiu.back': 'Back',
    'vaiu.email': 'Email',
    'vaiu.app.description.main': 'A social payments network that helps people share money, requests, and referral codes.',
    'vaiu.app.description.joining': 'Create an account to send, receive, and manage payments across the network.',
    'vaiu.creating_account': 'Creating account...',
    'vaiu.google_signup_success': 'Google sign-up successful',
    'vaiu.log_in': 'Log in',
    'vaiu.forgot_password.title': 'Forgot your password?',
    'vaiu.forgot_password.description': 'Enter your email address and we\'ll send you a link to reset your password.',
    'vaiu.forgot_password.send_reset_link': 'Send reset link',
    'vaiu.forgot_password.remember_password': 'Remember your password?',
    'vaiu.forgot_password.email_sent_title': 'Check your email',
    'vaiu.forgot_password.email_sent_description': 'We\'ve sent a password reset link to {{email}}',
    'vaiu.forgot_password.check_spam': 'If you don\'t see the email, check your spam folder.',
    'vaiu.forgot_password.try_different_email': 'Try a different email',
    'vaiu.back_to_login': 'Back to Login',
    'vaiu.new_password': 'New Password',
    'vaiu.confirm_password': 'Confirm Password',
    'vaiu.reset_password.form_title': 'Reset Your Password',
    'vaiu.reset_password.form_description': 'Enter your new password below',
    'vaiu.reset_password.submit': 'Reset Password',
    'vaiu.reset_password.resetting_password': 'Resetting Password...',
    'vaiu.reset_password.success_title': 'Password Reset Successful',
    'vaiu.reset_password.success_description': 'Your password has been reset successfully.',
    'vaiu.reset_password.need_help': 'Need help?',
    'vaiu.reset_password.request_new': 'Request a new reset link',
    'vaiu.continue_to_app': 'Continue to App',
    'vaiu.sending': 'Sending...',
    'vaiu.registration_success': 'Registration successful',
    'vaiu.registration_page.have_account_question': 'Already have an account?',
    'vaiu.signup_title': 'Sign up',
    'vaiu.signup.email_as_username_note': 'We use your email address as your username.',
    'vaiu.reset_password.title': 'Reset Your Password',
    'vaiu.reset_password.description': 'We will send a secure reset link to your email address.',
    'vaiu.reset_password.instructions': 'Enter the email associated with your account to receive a reset link.',
    'vaiu.reset_password.redirect_notice': 'You will be redirected after the reset link is opened.',
    'vaiu.reset_password.security_note': 'For your security, reset links expire after a short time.',
    'vaiu.reset_password.continue_button': 'Continue to Reset Password',
    'vaiu.resetting_password': 'Resetting password...',
    'vaiu.retype_password': 'Retype password',
    'vaiu.validation.fix_errors': 'Please fix the highlighted errors',
    'vaiu.validation.invalid_email': 'Please enter a valid email address',
    'vaiu.errors.try_again_later': 'Please try again later',
    'vaiu.errors.email_already_exists': 'An account with that email already exists',
    'vaiu.errors.username_already_exists': 'An account with that username already exists',
    'vaiu.passwords_not_match': 'Passwords do not match',
    'vaiu.password_strength.label': 'Password strength',
    'vaiu.password_strength.tips': 'Use a longer password with a mix of letters, numbers, and symbols.',
    'signup.passwordStrength.fair': 'Fair',
    'vaiu.core.dashboard': 'Dashboard',
    'vaiu.core.your_balances': 'Your Balances',
    'vaiu.core.send_money': 'Send Money',
    'vaiu.core.request_money': 'Request Money',
    'vaiu.core.redeem_token': 'Redeem Token',
    'vaiu.core.add_money': 'Add Money',
    'vaiu.core.view_all_transactions': 'View All Transactions',
    'vaiu.core.referral_tokens': 'Referral Tokens',
    'vaiu.core.payment_sources': 'Payment Sources',
    'vaiu.core.inbox': 'Inbox',
    'vaiu.core.loading': 'Loading...',
    'vaiu.core.auth_error': 'Authentication error:',
    'vaiu.token_slider.create_token': 'Create Token',
    'vaiu.token_slider.to_get_money': 'To Get Money',
    'vaiu.token_slider.to_give_money': 'To Give Money',
    'vaiu.token_slider.give_money': 'Create a token to give someone',
    'vaiu.token_slider.request_money': 'Request money from someone',
    'vaiu.token_slider.create_invoice': 'Create an invoice',
    'vaiu.token_slider.create_payment': 'Create a payment',
    'vaiu.token_slider.invoice': 'Invoice',
    'vaiu.token_slider.to_pay': 'To Pay',
    'vaiu.token_form.amount_to_give': 'Withdraw',
    'vaiu.token_form.amount_to_receive': 'Amount you will receive',
    'vaiu.token_form.advanced_options': 'Advanced Options',
    'vaiu.token_form.add_conditions': 'Add Conditions',
    'vaiu.payment.manage_accounts': 'Manage Accounts',
    'vaiu.payment.funding_sources': 'Funding Sources',
    'vaiu.payment.manage_funding_sources': 'Manage your funding sources',
    'vaiu.payment.add_payment_method': 'Add Payment Method',
    'vaiu.payment.choose_funding_description': 'Choose a secure funding method. Your information is encrypted.',
    'vaiu.payment.bank_account': 'Bank Account',
    'vaiu.payment.bank_account_description': 'Connect your bank account via Plaid',
    'vaiu.payment.no_fees': 'No Fees',
    'vaiu.payment.most_secure': 'Most Secure',
    'vaiu.payment.ach_transfers': 'ACH Transfers',
    'vaiu.payment.free': 'Free',
    'vaiu.payment.business_days': '1-3 business days',
    'vaiu.payment.credit_card': 'Credit/Debit Card',
    'vaiu.payment.credit_card_description': 'Pay by card via Stripe',
    'vaiu.payment.instant_transfers': 'Instant Transfers',
    'vaiu.payment.widely_accepted': 'Widely Accepted',
    'vaiu.payment.easy_setup': 'Easy Setup',
    'vaiu.payment.card_fee': '~2.9% + $0.30',
    'vaiu.payment.instant': 'Instant',
    'vaiu.payment.paypal': 'PayPal',
    'vaiu.payment.choose_method': 'Choose Method',
    'vaiu.payment.setup': 'Setup',
    'vaiu.payment.complete': 'Complete',
    'vaiu.payment.your_funding_sources': 'Your Funding Sources',
    'vaiu.payment.verified': 'Verified',
    'vaiu.payment.toast_token_created': 'Token Created',
    'vaiu.payment.toast_token_created_desc': 'Token {{key}} created successfully',
    'vaiu.payment.toast_payment_delivered_successfully': 'Payment Delivered Successfully',
    'vaiu.payment.toast_payment_delivered_successfully_desc': 'Your payment of {{amount}} {{currency}} has been delivered to {{name}}',
    'vaiu.payment.toast_payment_to_inbox': 'Payment Sent to Inbox',
    'vaiu.payment.toast_payment_to_inbox_desc': '{{name}} will review and accept your payment',
    'vaiu.payment.toast_recipient_not_found': 'Recipient Not Found',
    'vaiu.payment.toast_recipient_not_found_desc': 'The recipient could not be found. The token has been created and can be shared manually.',
    'vaiu.payment.toast_payment_delivered': 'Payment Delivered',
    'vaiu.payment.toast_payment_delivered_desc': 'Your payment has been delivered to {{name}}',
    'confirmIdentity.title': 'Confirm your identity',
    'confirmIdentity.prompt': 'We need to verify your name before connecting your bank. Please confirm:',
    'confirmIdentity.yourName': 'Is this your name?',
    'confirmIdentity.confirmButton': "Yes, that's me",
    'confirmIdentity.disclaimer': 'This name will be used to verify your bank account with our payment provider.',
    'confirmIdentity.requiredMessage': 'Your first and last name are required to add a bank account.',
    'confirmIdentity.updateProfileMessage': 'Please add your first and last name in your profile before continuing.',
    'confirmIdentity.updateProfile': 'Update my profile',
    'confirmIdentity.updateNameLink': "Not correct? Update your name in profile",
    'confirmIdentity.yes': 'Yes',
    'confirmIdentity.no': 'No',
    'vaiu.password_strength.very_weak': 'Very Weak',
    'vaiu.password_strength.weak': 'Weak',
    'vaiu.password_strength.fair': 'Fair',
    'vaiu.password_strength.medium': 'Medium',
    'vaiu.password_strength.strong': 'Strong',
    'vaiu.password_strength.very_strong': 'Very Strong',
    'vaiu.profile.autoAccept': 'Token Auto-Accept',
    'vaiu.profile.invoicePayment': 'Invoice Payment Preferences',
    'vaiu.faq.cancel_token_question': 'Can I cancel a token after creating it?',
    'vaiu.faq.cancel_token_answer': 'Yes. Some token types can be canceled before they are redeemed, depending on their status and conditions.',
  },
  fr: {
    'common.back': 'Retour',
    'common.next': 'Suivant',
    'common.save': 'Enregistrer',
    'common.submit': 'Soumettre',
    'common.cancel': 'Annuler',
    'common.delete': 'Supprimer',
    'common.edit': 'Modifier',
    'common.add': 'Ajouter',
    'common.remove': 'Retirer',
    'common.confirm': 'Confirmer',
    'common.close': 'Fermer',
    'common.loading': 'Chargement...',
    'common.error': 'Erreur',
    'common.success': 'Succès',
    'common.note': 'Note',
    'common.optional': 'Optionnel',
    'common.required': 'Requis',
    'common.message': 'Message',
    'common.recipient': 'Destinataire',
    'common.id': 'ID',
    'common.amount': 'Montant',
    'common.reference': 'Référence',
    'common.goBack': 'Retour',
    'common.transactionId': 'ID de Transaction',
    'common.currency': 'Devise',
    'common.loadingProfile': 'Chargement du profil...',
    'common.give_money_to': 'Donner de l\'argent à {{name}}',
    'common.give_money_to_stranger': 'Donner de l\'argent à cet inconnu',
    'common.kybVerified': 'KYB Vérifié',
    'common.kybPending': 'KYB En Attente',
    'common.kybRejected': 'KYB Rejeté',
    'common.notListed': 'Non Répertorié',
    'common.unknown': 'Inconnu',
    'userPublicProfile.loading': 'Chargement du profil...',
    'userPublicProfile.notFound': 'Profil Non Trouvé',
    'userPublicProfile.notFoundDesc': 'Profil utilisateur introuvable',
    'userPublicProfile.memberSince': 'Membre depuis',
    'userPublicProfile.sendMoney': 'Envoyer de l\'argent',
    'userPublicProfile.profileInformation': 'Informations du Profil',
    'userPublicProfile.userId': 'ID Utilisateur',
    'userPublicProfile.username': 'Nom d\'utilisateur',
    'userPublicProfile.notSet': 'Non défini',
    'userPublicProfile.roles': 'Rôles',
    'guest.continue_as_guest': 'Continuer en tant qu\'invité',
    'guest.login_description': 'Accès complet pendant 48 heures. Inscrivez-vous pour pouvoir vous connecter',
    'guest.upgrade_account': 'Mettre à niveau le compte',
    'guest.upgrade_prompt.title': 'Mettez à niveau votre compte invité',
    'guest.upgrade_prompt.description': 'Sauvegardez votre historique et débloquez toutes les fonctionnalités en vous inscrivant.',
    'guest.upgrade_prompt.continue': 'Continuer pour mettre à niveau',
    'guest.session_indicator.title': 'Session invité',
    'guest.session_indicator.time_left': 'Temps restant',
    'guest.session_indicator.sign_up_notice': 'Inscrivez-vous pour sauvegarder vos données et continuer à utiliser l\'application.',
    'guest.session_indicator.session_expired': 'Session expirée',
    'guest.session_indicator.session_expired_message': 'Votre session invité a expiré. Veuillez vous inscrire ou vous connecter pour continuer.',
    'guest.session_indicator.sign_up': 'S\'inscrire',
    'guest.logout.warning.title': 'Se déconnecter en tant qu\'invité ?',
    'guest.logout.warning.description': 'Si vous vous déconnectez, vous perdrez l\'accès à votre compte invité. Toutes les cartes que vous avez ajoutées et votre historique de transactions deviendront inaccessibles à moins que vous ne vous inscriviez pour un compte permanent.',
    'guest.logout.cancel': 'Annuler',
    'guest.logout.confirm': 'Se déconnecter quand même',
    'vaiu.sign_in': 'Se connecter',
    'vaiu.sign_up': 'S\'inscrire',
    'vaiu.or': 'ou',
    'vaiu.password': 'Mot de passe',
    'vaiu.forgot_password': 'Mot de passe oublié ?',
    'vaiu.signing_in': 'Connexion...',
    'vaiu.otp_login.title': 'Se connecter avec le téléphone',
    'vaiu.otp_login.description': 'Entrez votre numéro de téléphone pour recevoir un code à usage unique.',
    'vaiu.otp_login.phone_placeholder': 'Votre numéro de téléphone',
    'vaiu.otp_login.request_code': 'Demander le code',
    'vaiu.otp_login.requesting_code': 'Demande de code en cours...',
    'vaiu.otp_login.enter_code_title': 'Entrez le code de vérification',
    'vaiu.otp_login.enter_code_description': 'Un code a été envoyé au {{phoneNumber}}',
    'vaiu.otp_login.verify': 'Vérifier',
    'vaiu.otp_login.verifying': 'Vérification en cours...',
    'vaiu.otp_login.resend_code': 'Renvoyer le code',
    'vaiu.otp_login.resend_code_in': 'Renvoyer le code dans {{seconds}}s',
    'vaiu.otp_login.wrong_number': 'Mauvais numéro ?',
    'vaiu.back': 'Retour',
    'vaiu.email': 'Email',
    'vaiu.forgot_password.title': 'Mot de passe oublié ?',
    'vaiu.forgot_password.description': 'Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.',
    'vaiu.forgot_password.send_reset_link': 'Envoyer le lien de réinitialisation',
    'vaiu.forgot_password.remember_password': 'Vous vous souvenez de votre mot de passe ?',
    'vaiu.forgot_password.email_sent_title': 'Vérifiez votre email',
    'vaiu.forgot_password.email_sent_description': 'Nous avons envoyé un lien de réinitialisation de mot de passe à {{email}}',
    'vaiu.forgot_password.check_spam': 'Si vous ne voyez pas l\'email, vérifiez votre dossier spam.',
    'vaiu.forgot_password.try_different_email': 'Essayer un autre email',
    'vaiu.back_to_login': 'Retour à la connexion',
    'vaiu.new_password': 'Nouveau mot de passe',
    'vaiu.confirm_password': 'Confirmer le mot de passe',
    'vaiu.reset_password.form_title': 'Réinitialisez votre mot de passe',
    'vaiu.reset_password.form_description': 'Entrez votre nouveau mot de passe ci-dessous',
    'vaiu.reset_password.submit': 'Réinitialiser le mot de passe',
    'vaiu.reset_password.resetting_password': 'Réinitialisation du mot de passe...',
    'vaiu.reset_password.success_title': 'Mot de passe réinitialisé avec succès',
    'vaiu.reset_password.success_description': 'Votre mot de passe a été réinitialisé avec succès.',
    'vaiu.reset_password.need_help': 'Besoin d\'aide ?',
    'vaiu.reset_password.request_new': 'Demander un nouveau lien de réinitialisation',
    'vaiu.continue_to_app': 'Continuer vers l\'application',
    'vaiu.sending': 'Envoi en cours...',
    'vaiu.core.dashboard': 'Tableau de bord',
    'vaiu.core.your_balances': 'Vos soldes',
    'vaiu.core.send_money': 'Envoyer de l\'argent',
    'vaiu.core.request_money': 'Demander de l\'argent',
    'vaiu.core.redeem_token': 'Utiliser le jeton',
    'vaiu.core.add_money': 'Ajouter de l\'argent',
    'vaiu.core.view_all_transactions': 'Voir toutes les transactions',
    'vaiu.core.referral_tokens': 'Jetons de parrainage',
    'vaiu.core.payment_sources': 'Sources de paiement',
    'vaiu.core.inbox': 'Boîte de réception',
    'vaiu.core.loading': 'Chargement...',
    'vaiu.core.auth_error': 'Erreur d\'authentification :',
    'vaiu.token_slider.create_token': 'Créer un jeton',
    'vaiu.token_slider.to_get_money': 'Pour recevoir de l\'argent',
    'vaiu.token_slider.to_give_money': 'Pour donner de l\'argent',
    'vaiu.token_slider.give_money': 'Créer un jeton pour donner à quelqu\'un',
    'vaiu.token_slider.request_money': 'Demander de l\'argent à quelqu\'un',
    'vaiu.token_slider.create_invoice': 'Créer une facture',
    'vaiu.token_slider.create_payment': 'Créer un paiement',
    'vaiu.token_slider.invoice': 'Facture',
    'vaiu.token_slider.to_pay': 'Pour payer',
    'vaiu.token_form.amount_to_give': 'Retrait',
    'vaiu.token_form.amount_to_receive': 'Montant que vous recevrez',
    'vaiu.token_form.advanced_options': 'Options avancées',
    'vaiu.token_form.add_conditions': 'Ajouter des conditions',
    'vaiu.payment.manage_accounts': 'Gérer les comptes',
    'vaiu.payment.funding_sources': 'Sources de financement',
    'vaiu.payment.manage_funding_sources': 'Gérez vos sources de financement',
    'vaiu.payment.add_payment_method': 'Ajouter un mode de paiement',
    'vaiu.payment.choose_funding_description': 'Choisissez un mode de financement sécurisé. Vos informations sont cryptées.',
    'vaiu.payment.bank_account': 'Compte bancaire',
    'vaiu.payment.bank_account_description': 'Connectez votre compte bancaire via Plaid',
    'vaiu.payment.no_fees': 'Sans frais',
    'vaiu.payment.most_secure': 'Le plus sécurisé',
    'vaiu.payment.ach_transfers': 'Virements ACH',
    'vaiu.payment.free': 'Gratuit',
    'vaiu.payment.business_days': '1-3 jours ouvrables',
    'vaiu.payment.credit_card': 'Carte de crédit/débit',
    'vaiu.payment.credit_card_description': 'Payez par carte via Stripe',
    'vaiu.payment.instant_transfers': 'Virements instantanés',
    'vaiu.payment.widely_accepted': 'Largement accepté',
    'vaiu.payment.easy_setup': 'Configuration facile',
    'vaiu.payment.card_fee': '~2.9% + 0,30 $',
    'vaiu.payment.instant': 'Instantané',
    'vaiu.payment.paypal': 'PayPal',
    'vaiu.payment.choose_method': 'Choisir la méthode',
    'vaiu.payment.setup': 'Configuration',
    'vaiu.payment.complete': 'Terminé',
    'vaiu.payment.your_funding_sources': 'Vos sources de financement',
    'vaiu.payment.verified': 'Vérifié',
    'vaiu.payment.toast_token_created': 'Token Créé',
    'vaiu.payment.toast_token_created_desc': 'Token {{key}} créé avec succès',
    'vaiu.payment.toast_payment_delivered_successfully': 'Paiement Livré avec Succès',
    'vaiu.payment.toast_payment_delivered_successfully_desc': 'Votre paiement de {{amount}} {{currency}} a été livré à {{name}}',
    'vaiu.payment.toast_payment_to_inbox': 'Paiement Envoyé à la Boîte de Réception',
    'vaiu.payment.toast_payment_to_inbox_desc': '{{name}} examinera et acceptera votre paiement',
    'vaiu.payment.toast_recipient_not_found': 'Destinataire Non Trouvé',
    'vaiu.payment.toast_recipient_not_found_desc': 'Le destinataire n\'a pas pu être trouvé. Le token a été créé et peut être partagé manuellement.',
    'vaiu.payment.toast_payment_delivered': 'Paiement Livré',
    'vaiu.payment.toast_payment_delivered_desc': 'Votre paiement a été livré à {{name}}',
    'confirmIdentity.title': 'Confirmez votre identité',
    'confirmIdentity.prompt': 'Nous devons vérifier votre nom avant de connecter votre banque. Veuillez confirmer :',
    'confirmIdentity.yourName': 'Est-ce votre nom ?',
    'confirmIdentity.confirmButton': "Oui, c'est moi",
    'confirmIdentity.disclaimer': "Ce nom sera utilisé pour vérifier votre compte bancaire auprès de notre prestataire de paiement.",
    'confirmIdentity.requiredMessage': 'Votre prénom et nom sont requis pour ajouter un compte bancaire.',
    'confirmIdentity.updateProfileMessage': 'Veuillez ajouter votre prénom et nom dans votre profil avant de continuer.',
    'confirmIdentity.updateProfile': 'Mettre à jour mon profil',
    'confirmIdentity.updateNameLink': "Pas correct ? Mettez à jour votre nom dans le profil",
    'confirmIdentity.yes': 'Oui',
    'confirmIdentity.no': 'Non',
    'vaiu.password_strength.very_weak': 'Très Faible',
    'vaiu.password_strength.weak': 'Faible',
    'vaiu.password_strength.medium': 'Moyen',
    'vaiu.password_strength.strong': 'Fort',
    'vaiu.password_strength.very_strong': 'Très Fort',
    'vaiu.profile.autoAccept': 'Acceptation Automatique de Jetons',
    'vaiu.profile.invoicePayment': 'Préférences de Paiement de Facture',
  },
  es: {
    'common.back': 'Atrás',
    'common.next': 'Siguiente',
    'common.save': 'Guardar',
    'common.submit': 'Enviar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.add': 'Añadir',
    'common.remove': 'Eliminar',
    'common.confirm': 'Confirmar',
    'common.close': 'Cerrar',
    'common.loading': 'Cargando...',
    'common.error': 'Error',
    'common.success': 'Éxito',
    'common.note': 'Nota',
    'common.optional': 'Opcional',
    'common.required': 'Obligatorio',
    'common.message': 'Mensaje',
    'common.recipient': 'Destinatario',
    'common.id': 'ID',
    'common.amount': 'Cantidad',
    'common.reference': 'Referencia',
    'common.goBack': 'Volver',
    'common.transactionId': 'ID de Transacción',
    'common.currency': 'Moneda',
    'common.loadingProfile': 'Cargando perfil...',
    'common.give_money_to': 'Dar dinero a {{name}}',
    'common.give_money_to_stranger': 'Dar dinero a este desconocido',
    'common.kybVerified': 'KYB Verificado',
    'common.kybPending': 'KYB Pendiente',
    'common.kybRejected': 'KYB Rechazado',
    'common.notListed': 'No Listado',
    'common.unknown': 'Desconocido',
    'userPublicProfile.loading': 'Cargando perfil...',
    'userPublicProfile.notFound': 'Perfil No Encontrado',
    'userPublicProfile.notFoundDesc': 'Perfil de usuario no encontrado',
    'userPublicProfile.memberSince': 'Miembro desde',
    'userPublicProfile.sendMoney': 'Enviar Dinero',
    'userPublicProfile.profileInformation': 'Información del Perfil',
    'userPublicProfile.userId': 'ID de Usuario',
    'userPublicProfile.username': 'Nombre de Usuario',
    'userPublicProfile.notSet': 'No establecido',
    'userPublicProfile.roles': 'Roles',
    'guest.continue_as_guest': 'Continuar como invitado',
    'guest.login_description': 'Acceso completo durante 48 horas. Regístrate para poder iniciar sesión',
    'guest.upgrade_account': 'Actualizar cuenta',
    'guest.upgrade_prompt.title': 'Actualiza tu cuenta de invitado',
    'guest.upgrade_prompt.description': 'Guarda tu historial y desbloquea todas las funciones registrándote.',
    'guest.upgrade_prompt.continue': 'Continuar para actualizar',
    'guest.session_indicator.title': 'Sesión de invitado',
    'guest.session_indicator.time_left': 'Tiempo restante',
    'guest.session_indicator.sign_up_notice': 'Regístrate para guardar tus datos y seguir usando la aplicación.',
    'guest.session_indicator.session_expired': 'Sesión expirada',
    'guest.session_indicator.session_expired_message': 'Tu sesión de invitado ha expirado. Por favor, regístrate o inicia sesión para continuar.',
    'guest.session_indicator.sign_up': 'Registrarse',
    'guest.logout.warning.title': '¿Cerrar sesión como invitado?',
    'guest.logout.warning.description': 'Si cierras sesión, perderás el acceso a tu cuenta de invitado. Todas las tarjetas que hayas agregado y tu historial de transacciones se volverán inaccesibles a menos que te registres para una cuenta permanente.',
    'guest.logout.cancel': 'Cancelar',
    'guest.logout.confirm': 'Cerrar sesión de todos modos',
    'vaiu.sign_in': 'Iniciar sesión',
    'vaiu.sign_up': 'Registrarse',
    'vaiu.or': 'o',
    'vaiu.password': 'Contraseña',
    'vaiu.forgot_password': '¿Has olvidado la contraseña?',
    'vaiu.signing_in': 'Iniciando sesión...',
    'vaiu.otp_login.title': 'Iniciar sesión con el teléfono',
    'vaiu.otp_login.description': 'Introduce tu número de teléfono para recibir un código de un solo uso.',
    'vaiu.otp_login.phone_placeholder': 'Tu número de teléfono',
    'vaiu.otp_login.request_code': 'Solicitar código',
    'vaiu.otp_login.requesting_code': 'Solicitando código...',
    'vaiu.otp_login.enter_code_title': 'Introduce el código de verificación',
    'vaiu.otp_login.enter_code_description': 'Se ha enviado un código a {{phoneNumber}}',
    'vaiu.otp_login.verify': 'Verificar',
    'vaiu.otp_login.verifying': 'Verificando...',
    'vaiu.otp_login.resend_code': 'Reenviar código',
    'vaiu.otp_login.resend_code_in': 'Reenviar código en {{seconds}}s',
    'vaiu.otp_login.wrong_number': '¿Número equivocado?',
    'vaiu.back': 'Atrás',
    'vaiu.email': 'Email',
    'vaiu.forgot_password.title': '¿Has olvidado la contraseña?',
    'vaiu.forgot_password.description': 'Introduce tu dirección de email y te enviaremos un enlace para restablecer tu contraseña.',
    'vaiu.forgot_password.send_reset_link': 'Enviar enlace de restablecimiento',
    'vaiu.forgot_password.remember_password': '¿Recuerdas tu contraseña?',
    'vaiu.forgot_password.email_sent_title': 'Revisa tu email',
    'vaiu.forgot_password.email_sent_description': 'Hemos enviado un enlace de restablecimiento de contraseña a {{email}}',
    'vaiu.forgot_password.check_spam': 'Si no ves el email, comprueba tu carpeta de spam.',
    'vaiu.forgot_password.try_different_email': 'Probar con otro email',
    'vaiu.back_to_login': 'Volver a iniciar sesión',
    'vaiu.new_password': 'Nueva contraseña',
    'vaiu.confirm_password': 'Confirmar contraseña',
    'vaiu.reset_password.form_title': 'Restablece tu contraseña',
    'vaiu.reset_password.form_description': 'Introduce tu nueva contraseña a continuación',
    'vaiu.reset_password.submit': 'Restablecer contraseña',
    'vaiu.reset_password.resetting_password': 'Restableciendo contraseña...',
    'vaiu.reset_password.success_title': 'Contraseña restablecida con éxito',
    'vaiu.reset_password.success_description': 'Tu contraseña ha sido restablecida con éxito.',
    'vaiu.reset_password.need_help': '¿Necesitas ayuda?',
    'vaiu.reset_password.request_new': 'Solicitar un nuevo enlace de restablecimiento',
    'vaiu.continue_to_app': 'Continuar a la aplicación',
    'vaiu.sending': 'Enviando...',
    'vaiu.core.dashboard': 'Panel de control',
    'vaiu.core.your_balances': 'Tus saldos',
    'vaiu.core.send_money': 'Enviar dinero',
    'vaiu.core.request_money': 'Solicitar dinero',
    'vaiu.core.redeem_token': 'Canjear token',
    'vaiu.core.add_money': 'Añadir dinero',
    'vaiu.core.view_all_transactions': 'Ver todas las transacciones',
    'vaiu.core.referral_tokens': 'Tokens de referencia',
    'vaiu.core.payment_sources': 'Fuentes de pago',
    'vaiu.core.inbox': 'Bandeja de entrada',
    'vaiu.core.loading': 'Cargando...',
    'vaiu.core.auth_error': 'Error de autenticación:',
    'vaiu.token_slider.create_token': 'Crear Token',
    'vaiu.token_slider.to_get_money': 'Para Recibir Dinero',
    'vaiu.token_slider.to_give_money': 'Para Dar Dinero',
    'vaiu.token_slider.give_money': 'Crear un token para dar a alguien',
    'vaiu.token_slider.request_money': 'Solicitar dinero a alguien',
    'vaiu.token_slider.create_invoice': 'Crear una factura',
    'vaiu.token_slider.create_payment': 'Crear un pago',
    'vaiu.token_slider.invoice': 'Factura',
    'vaiu.token_slider.to_pay': 'Para Pagar',
    'vaiu.token_form.amount_to_give': 'Retiro',
    'vaiu.token_form.amount_to_receive': 'Cantidad que recibirás',
    'vaiu.token_form.advanced_options': 'Opciones Avanzadas',
    'vaiu.token_form.add_conditions': 'Agregar Condiciones',
    'vaiu.payment.manage_accounts': 'Gestionar Cuentas',
    'vaiu.payment.funding_sources': 'Fuentes de financiación',
    'vaiu.payment.manage_funding_sources': 'Gestiona tus fuentes de financiación',
    'vaiu.payment.add_payment_method': 'Añadir método de pago',
    'vaiu.payment.choose_funding_description': 'Elige un método de financiación seguro. Tu información está encriptada.',
    'vaiu.payment.bank_account': 'Cuenta bancaria',
    'vaiu.payment.bank_account_description': 'Conecta tu cuenta bancaria a través de Plaid',
    'vaiu.payment.no_fees': 'Sin comisiones',
    'vaiu.payment.most_secure': 'Más seguro',
    'vaiu.payment.ach_transfers': 'Transferencias ACH',
    'vaiu.payment.free': 'Gratis',
    'vaiu.payment.business_days': '1-3 días laborables',
    'vaiu.payment.credit_card': 'Tarjeta de crédito/débito',
    'vaiu.payment.credit_card_description': 'Paga con tarjeta a través de Stripe',
    'vaiu.payment.instant_transfers': 'Transferencias instantáneas',
    'vaiu.payment.widely_accepted': 'Ampliamente aceptado',
    'vaiu.payment.easy_setup': 'Configuración fácil',
    'vaiu.payment.card_fee': '~2.9% + 0,30 $',
    'vaiu.payment.instant': 'Instantáneo',
    'vaiu.payment.paypal': 'PayPal',
    'vaiu.payment.choose_method': 'Elegir método',
    'vaiu.payment.setup': 'Configuración',
    'vaiu.payment.complete': 'Completado',
    'vaiu.payment.your_funding_sources': 'Tus fuentes de financiación',
    'vaiu.payment.verified': 'Verificado',
    'vaiu.payment.toast_token_created': 'Token Creado',
    'vaiu.payment.toast_token_created_desc': 'Token {{key}} creado exitosamente',
    'vaiu.payment.toast_payment_delivered_successfully': 'Pago Entregado Exitosamente',
    'vaiu.payment.toast_payment_delivered_successfully_desc': 'Tu pago de {{amount}} {{currency}} ha sido entregado a {{name}}',
    'vaiu.payment.toast_payment_to_inbox': 'Pago Enviado a la Bandeja de Entrada',
    'vaiu.payment.toast_payment_to_inbox_desc': '{{name}} revisará y aceptará tu pago',
    'vaiu.payment.toast_recipient_not_found': 'Destinatario No Encontrado',
    'vaiu.payment.toast_recipient_not_found_desc': 'No se pudo encontrar al destinatario. El token ha sido creado y puede compartirse manualmente.',
    'vaiu.payment.toast_payment_delivered': 'Pago Entregado',
    'vaiu.payment.toast_payment_delivered_desc': 'Tu pago ha sido entregado a {{name}}',
    'confirmIdentity.title': 'Confirme su identidad',
    'confirmIdentity.prompt': 'Necesitamos verificar su nombre antes de conectar su banco. Por favor confirme:',
    'confirmIdentity.yourName': '¿Es este su nombre?',
    'confirmIdentity.confirmButton': "Sí, soy yo",
    'confirmIdentity.disclaimer': 'Este nombre se utilizará para verificar su cuenta bancaria con nuestro proveedor de pagos.',
    'confirmIdentity.requiredMessage': 'Su nombre y apellido son obligatorios para añadir una cuenta bancaria.',
    'confirmIdentity.updateProfileMessage': 'Por favor añada su nombre y apellido en su perfil antes de continuar.',
    'confirmIdentity.updateProfile': 'Actualizar mi perfil',
    'confirmIdentity.updateNameLink': "¿No es correcto? Actualice su nombre en el perfil",
    'confirmIdentity.yes': 'Sí',
    'confirmIdentity.no': 'No',
    'vaiu.password_strength.very_weak': 'Muy Débil',
    'vaiu.password_strength.weak': 'Débil',
    'vaiu.password_strength.medium': 'Medio',
    'vaiu.password_strength.strong': 'Fuerte',
    'vaiu.password_strength.very_strong': 'Muy Fuerte',
  },
  sq: {
    'common.back': 'Kthehu',
    'common.next': 'Tjetra',
    'common.save': 'Ruaj',
    'common.submit': 'Postoj',
    'common.cancel': 'Anulo',
    'common.delete': 'Fshij',
    'common.edit': 'Ndrysho',
    'common.add': 'Shto',
    'common.remove': 'Hiq',
    'common.confirm': 'Konfirmo',
    'common.close': 'Mbyll',
    'common.loading': 'Duke punuar...',
    'common.error': 'Gabim',
    'common.success': 'Sukses',
    'common.note': 'Nota',
    'common.optional': 'Opsional',
    'common.required': 'E obbligatoriu',
    'common.message': 'Mesazh',
    'common.recipient': 'Marrësi',
    'common.id': 'ID',
    'common.amount': 'Shuma',
    'common.reference': 'Referencë',
    'common.goBack': 'Kthehu',
    'common.transactionId': 'ID e Transaksionit',
    'common.currency': 'Valuta',
    'common.loadingProfile': 'Duke ngarkuar profilin...',
    'common.give_money_to': 'Jep para për {{name}}',
    'common.give_money_to_stranger': 'Jep para këtij të panjohuri',
    'common.kybVerified': 'KYB i Verifikuar',
    'common.kybPending': 'KYB në Pritje',
    'common.kybRejected': 'KYB i Refuzuar',
    'common.notListed': 'Nuk është Listuar',
    'common.unknown': 'E Panjohur',
    'userPublicProfile.loading': 'Duke ngarkuar profilin...',
    'userPublicProfile.notFound': 'Profili Nuk u Gjet',
    'userPublicProfile.notFoundDesc': 'Profili i përdoruesit nuk u gjet',
    'userPublicProfile.memberSince': 'Anëtar që',
    'userPublicProfile.sendMoney': 'Dërgo Para',
    'userPublicProfile.profileInformation': 'Informacioni i Profilit',
    'userPublicProfile.userId': 'ID e Përdoruesit',
    'userPublicProfile.username': 'Emri i Përdoruesit',
    'userPublicProfile.notSet': 'Nuk është vendosur',
    'userPublicProfile.roles': 'Rolet',
    'guest.continue_as_guest': 'Kontinue si si guest',
    'guest.login_description': 'Përshëndetje për 48 orë. Regjistrohu për të mundësuar të hyjë',
    'guest.upgrade_account': 'Nëvojë llogari',
    'guest.upgrade_prompt.title': 'Nëvojë llogari për tuaj llogari guest',
    'guest.upgrade_prompt.description': 'Ruajni historikun tuaj dhe bëni të qasur për të gjitha veçoritë duke u regjistruar.',
    'guest.upgrade_prompt.continue': 'Vazhdo me nëvojë',
    'guest.session_indicator.title': 'Sesioni guest',
    'guest.session_indicator.time_left': 'Koha e mbetur',
    'guest.session_indicator.sign_up_notice': 'Regjistrohu për të ruajtën të dhënat dhe të vazhdën përdorimin e aplikacionit.',
    'guest.session_indicator.session_expired': 'Sesioni ka e kaluar',
    'guest.session_indicator.session_expired_message': 'Sesioni guest ka e kaluar. Ju lutemi, regjistrohu ose hyni në llogari për të vazhdën.',
    'guest.session_indicator.sign_up': 'Regjistrohu',
    'guest.logout.warning.title': 'Dilni si mysafir?',
    'guest.logout.warning.description': 'Nëse dilni, do të humbni qasjen në llogarinë tuaj të mysafirit. Çdo kartë që keni shtuar dhe historiku i transaksioneve tuaja do të bëhen të paarritshme përveç nëse regjistroheni për një llogari të përhershme.',
    'guest.logout.cancel': 'Anulo',
    'guest.logout.confirm': 'Dilni gjithsesi',
    'vaiu.sign_in': 'Hyni',
    'vaiu.sign_up': 'Regjistrohu',
    'vaiu.or': 'ose',
    'vaiu.password': 'Fjalëkalimi',
    'vaiu.forgot_password': 'A jeni harruar fjalëkalimin?',
    'vaiu.signing_in': 'Hyni në proces...',
    'vaiu.otp_login.title': 'Hyni me numrin e telefonit',
    'vaiu.otp_login.description': 'Shkruani numrin e telefonit tuaj për të marre një kod të vetëm përdoruesit.',
    'vaiu.otp_login.phone_placeholder': 'Numri i telefonit tuaj',
    'vaiu.otp_login.request_code': 'Kërko kod',
    'vaiu.otp_login.requesting_code': 'Duke kërkuar kod...',
    'vaiu.otp_login.enter_code_title': 'Shkruani kodun e verifikimit',
    'vaiu.otp_login.enter_code_description': 'Një kod u është dërguar në {{phoneNumber}}',
    'vaiu.otp_login.verify': 'Verifiko',
    'vaiu.otp_login.verifying': 'Duke verifikuar...',
    'vaiu.otp_login.resend_code': 'Dërgo kodin përsëri',
    'vaiu.otp_login.resend_code_in': 'Dërgo kodin përsëri në {{seconds}}s',
    'vaiu.otp_login.wrong_number': 'Numri i gabuar?',
    'vaiu.back': 'Kthehu',
    'vaiu.email': 'Email',
    'vaiu.forgot_password.title': 'A jeni harruar fjalëkalimin?',
    'vaiu.forgot_password.description': 'Shkruani adresën tuaj email për të dërguar një lidhje për të rikthyer fjalëkalimin tuaj.',
    'vaiu.forgot_password.send_reset_link': 'Dërgo lidhjen e rikthyer',
    'vaiu.forgot_password.remember_password': 'A jeni menduar fjalëkalimin tuaj?',
    'vaiu.forgot_password.email_sent_title': 'Shikoni email-in tuaj',
    'vaiu.forgot_password.email_sent_description': 'Ju kemi dërguar një lidhje për rikthyer fjalëkalimin tuaj në {{email}}',
    'vaiu.forgot_password.check_spam': 'Nëse nuk shihni email-in, shikoni dossierin tuaj spam.',
    'vaiu.forgot_password.try_different_email': 'Provo një email të tjera',
    'vaiu.back_to_login': 'Kthehu në hyrje',
    'vaiu.new_password': 'Fjalëkalimi i ri',
    'vaiu.confirm_password': 'Konfirmo fjalëkalimin',
    'vaiu.reset_password.form_title': 'Rikthe fjalëkalimin tuaj',
    'vaiu.reset_password.form_description': 'Shkruani fjalëkalimin tuaj poshtë',
    'vaiu.reset_password.submit': 'Rikthe fjalëkalimin',
    'vaiu.reset_password.resetting_password': 'Rikthja fjalëkalimit...',
    'vaiu.reset_password.success_title': 'Fjalëkalimi i rikthyer me sukses',
    'vaiu.reset_password.success_description': 'Fjalëkalimi juaj u është rikthyer me sukses.',
    'vaiu.reset_password.need_help': 'A ju nevojitet ndihmë?',
    'vaiu.reset_password.request_new': 'Kërko një lidhje të rikthyer',
    'vaiu.continue_to_app': 'Vazhdo tek aplikacioni',
    'vaiu.sending': 'Duke dërguar...',
    'vaiu.core.dashboard': 'Paneli i kontrollit',
    'vaiu.core.your_balances': 'Bilanci juaj',
    'vaiu.core.send_money': 'Dërgo para',
    'vaiu.core.request_money': 'Kërko para',
    'vaiu.core.redeem_token': 'Përdorni tokenin',
    'vaiu.core.add_money': 'Shto para',
    'vaiu.core.view_all_transactions': 'Shikoni të gjitha transaksionet',
    'vaiu.core.referral_tokens': 'Tokens referimi',
    'vaiu.core.payment_sources': 'Burime të pagesës',
    'vaiu.core.inbox': 'Sanndër',
    'vaiu.core.loading': 'Duke punuar...',
    'vaiu.core.auth_error': 'Gabim autentifikimi:',
    'vaiu.token_slider.create_token': 'Krijo Token',
    'vaiu.token_slider.to_get_money': 'Për të Marrë Para',
    'vaiu.token_slider.to_give_money': 'Për të Dhënë Para',
    'vaiu.token_slider.give_money': 'Krijo një token për t\'i dhënë dikujt',
    'vaiu.token_slider.request_money': 'Kërko para nga dikush',
    'vaiu.token_slider.create_invoice': 'Krijo një faturë',
    'vaiu.token_slider.create_payment': 'Krijo një pagesë',
    'vaiu.token_slider.invoice': 'Faturë',
    'vaiu.token_slider.to_pay': 'Për të Paguar',
    'vaiu.token_form.amount_to_give': 'Tërheqje',
    'vaiu.token_form.amount_to_receive': 'Shuma që do të merrni',
    'vaiu.token_form.advanced_options': 'Opsione të Avancuara',
    'vaiu.token_form.add_conditions': 'Shto Kushte',
    'vaiu.payment.manage_accounts': 'Menaxho Llogaritë',
    'vaiu.payment.funding_sources': 'Burimi i financimit',
    'vaiu.payment.manage_funding_sources': 'Menaxhoni burimet e financimit',
    'vaiu.payment.add_payment_method': 'Shtoni një mënyrë të pagesës',
    'vaiu.payment.choose_funding_description': 'Zgjidhni një mënyrë të sigurtë financimi. Të dhënat tuaja janë kriptuar.',
    'vaiu.payment.bank_account': 'Bankën e llogarisë',
    'vaiu.payment.bank_account_description': 'Konekto bankën e llogarisë tuaj me rregullime të sigura',
    'vaiu.payment.no_fees': 'Pa më shpenzime',
    'vaiu.payment.most_secure': 'Më i sigurt',
    'vaiu.payment.ach_transfers': 'Virement ACH',
    'vaiu.payment.free': 'I lirë',
    'vaiu.payment.business_days': '1-3 ditë të bizheta',
    'vaiu.payment.credit_card': 'Karta kredit ose debit',
    'vaiu.payment.credit_card_description': 'Shtoni Visa, Mastercard ose karta të mëdha tjetër',
    'vaiu.payment.instant_transfers': 'Transfer instant',
    'vaiu.payment.widely_accepted': 'Largësisht e pranuar',
    'vaiu.payment.easy_setup': 'Rregullime të lehta',
    'vaiu.payment.card_fee': '2,9% + $0,30',
    'vaiu.payment.instant': 'I instan',
    'vaiu.payment.paypal': 'PayPal',
    'vaiu.payment.choose_method': 'Zgjidhni metod',
    'vaiu.payment.setup': 'Rregullime',
    'vaiu.payment.complete': 'Përfundimi',
    'vaiu.payment.your_funding_sources': 'Burimet tuaja të financimit',
    'vaiu.payment.verified': 'I Verifikuar',
    'vaiu.payment.toast_token_created': 'Token i Krijuar',
    'vaiu.payment.toast_token_created_desc': 'Tokeni {{key}} u krijua me sukses',
    'vaiu.payment.toast_payment_delivered_successfully': 'Pagesa u Dorëzua me Sukses',
    'vaiu.payment.toast_payment_delivered_successfully_desc': 'Pagesa juaj prej {{amount}} {{currency}} u dorëzua te {{name}}',
    'vaiu.payment.toast_payment_to_inbox': 'Pagesa u Dërgua në Kutinë e Postës',
    'vaiu.payment.toast_payment_to_inbox_desc': '{{name}} do të shqyrtojë dhe pranojë pagesën tuaj',
    'vaiu.payment.toast_recipient_not_found': 'Marrësi Nuk u Gjet',
    'vaiu.payment.toast_recipient_not_found_desc': 'Marrësi nuk mund të gjendet. Tokeni u krijua dhe mund të ndahet manualisht.',
    'vaiu.payment.toast_payment_delivered': 'Pagesa u Dorëzua',
    'vaiu.payment.toast_payment_delivered_desc': 'Pagesa juaj u dorëzua te {{name}}',
    'confirmIdentity.title': 'Konfirmoni identitetin tuaj',
    'confirmIdentity.prompt': 'Duhet të verifikojmë emrin tuaj përpara se të lidhim bankën. Ju lutemi konfirmoni:',
    'confirmIdentity.yourName': 'A është ky emri juaj?',
    'confirmIdentity.confirmButton': "Po, jam unë",
    'confirmIdentity.disclaimer': 'Ky emër do të përdoret për të verifikuar llogarinë tuaj bankare me ofruesin tonë të pagesave.',
    'confirmIdentity.requiredMessage': 'Emri dhe mbiemri juaj janë të nevojshëm për të shtuar një llogari bankare.',
    'confirmIdentity.updateProfileMessage': 'Ju lutemi shtoni emrin dhe mbiemrin në profilin tuaj përpara se të vazhdoni.',
    'confirmIdentity.updateProfile': 'Përditëso profilin tim',
    'confirmIdentity.updateNameLink': "Nuk është i saktë? Përditësoni emrin në profil",
    'confirmIdentity.yes': 'Po',
    'confirmIdentity.no': 'Jo',
    'vaiu.password_strength.very_weak': 'Very Weak',
    'vaiu.password_strength.weak': 'Weak',
    'vaiu.password_strength.medium': 'Medium',
    'vaiu.password_strength.strong': 'Strong',
    'vaiu.password_strength.very_strong': 'Very Strong',
  },
  ru: {
    'common.back': 'Назад',
    'common.next': 'Далее',
    'common.save': 'Сохранить',
    'common.submit': 'Послать',
    'common.cancel': 'Отмена',
    'common.delete': 'Удалить',
    'common.edit': 'Редактировать',
    'common.add': 'Добавить',
    'common.remove': 'Удалить',
    'common.confirm': 'Подтвердить',
    'common.close': 'Закрыть',
    'common.loading': 'Загрузка...',
    'common.error': 'Ошибка',
    'common.success': 'Успех',
    'common.note': 'Примечание',
    'common.optional': 'Опционально',
    'common.required': 'Обязательно',
    'common.message': 'Сообщение',
    'common.recipient': 'Получатель',
    'common.id': 'ID',
    'common.amount': 'Сумма',
    'common.reference': 'Ссылка',
    'common.goBack': 'Назад',
    'common.transactionId': 'ID Транзакции',
    'common.currency': 'Валюта',
    'common.loadingProfile': 'Загрузка профиля...',
    'common.give_money_to': 'Дать деньги {{name}}',
    'common.give_money_to_stranger': 'Дать деньги этому незнакомцу',
    'common.kybVerified': 'KYB Проверен',
    'common.kybPending': 'KYB В Ожидании',
    'common.kybRejected': 'KYB Отклонен',
    'common.notListed': 'Не В Списке',
    'common.unknown': 'Неизвестно',
    'userPublicProfile.loading': 'Загрузка профиля...',
    'userPublicProfile.notFound': 'Профиль Не Найден',
    'userPublicProfile.notFoundDesc': 'Профиль пользователя не найден',
    'userPublicProfile.memberSince': 'Участник с',
    'userPublicProfile.sendMoney': 'Отправить Деньги',
    'userPublicProfile.profileInformation': 'Информация о Профиле',
    'userPublicProfile.userId': 'ID Пользователя',
    'userPublicProfile.username': 'Имя Пользователя',
    'userPublicProfile.notSet': 'Не установлено',
    'userPublicProfile.roles': 'Роли',
    'guest.continue_as_guest': 'Настави као гост',
    'guest.login_description': 'Пуни приступ 48 сати. Региструјте се да бисте могли да се пријавите',
    'guest.upgrade_account': 'Надогради налог',
    'guest.upgrade_prompt.title': 'Надогради свој гостујући налог',
    'guest.upgrade_prompt.description': 'Сачувајте своју историју и откључајте све функције регистрацијом.',
    'guest.upgrade_prompt.continue': 'Настави са надоградњом',
    'guest.session_indicator.title': 'Гостујућа сесија',
    'guest.session_indicator.time_left': 'Преостало време',
    'guest.session_indicator.sign_up_notice': 'Пријавите се да бисте сачували своје податке и наставили да користите апликацију.',
    'guest.session_indicator.session_expired': 'Сесија је истекла',
    'guest.session_indicator.session_expired_message': 'Ваша гостујућа сесија је истекла. Пријавите се или региструјте да бисте наставили.',
    'guest.session_indicator.sign_up': 'Пријавите се',
    'vaiu.sign_in': 'Пријавите се',
    'vaiu.sign_up': 'Регистрация App VAIU',
    'vaiu.or': 'ИЛИ',
    'vaiu.password': 'Пароль',
    'vaiu.forgot_password': 'Забыли пароль?',
    'vaiu.signing_in': 'Вход в процессе...',
    'vaiu.otp_login.title': 'Вход с помощью телефона',
    'vaiu.otp_login.description': 'Введите свой номер телефона, чтобы получить одноразовый код.',
    'vaiu.otp_login.phone_placeholder': 'Ваш номер телефона',
    'vaiu.otp_login.request_code': 'Запросить код',
    'vaiu.otp_login.requesting_code': 'Запрос кода...',
    'vaiu.otp_login.enter_code_title': 'Введите код подтверждения',
    'vaiu.otp_login.enter_code_description': 'Код отправлен на {{phoneNumber}}',
    'vaiu.otp_login.verify': 'Подтвердить',
    'vaiu.otp_login.verifying': 'Проверка...',
    'vaiu.otp_login.resend_code': 'Отправить код снова',
    'vaiu.otp_login.resend_code_in': 'Отправить код еще раз через {{seconds}}с',
    'vaiu.otp_login.wrong_number': 'Неверный номер?',
    'vaiu.back': 'Назад',
    'vaiu.email': 'Email',
    'vaiu.forgot_password.title': 'Забыли пароль?',
    'vaiu.forgot_password.description': 'Введите вашу электронную почту, чтобы мы отправили вам ссылку для сброса пароля.',
    'vaiu.forgot_password.send_reset_link': 'Отправить ссылку для сброса пароля',
    'vaiu.forgot_password.remember_password': 'Вспомните ваш пароль?',
    'vaiu.forgot_password.email_sent_title': 'Проверьте свою электронную почту',
    'vaiu.forgot_password.email_sent_description': 'Мы отправили вам ссылку для сброса пароля на {{email}}',
    'vaiu.forgot_password.check_spam': 'Если вы не видите письмо, проверьте папку спама.',
    'vaiu.forgot_password.try_different_email': 'Попробуйте другой email',
    'vaiu.back_to_login': 'Назад на приветствие',
    'vaiu.new_password': 'Новый пароль',
    'vaiu.confirm_password': 'Подтвердите пароль',
    'vaiu.reset_password.form_title': 'Сбросить пароль',
    'vaiu.reset_password.form_description': 'Введите новый пароль ниже',
    'vaiu.reset_password.submit': 'Сбросить пароль',
    'vaiu.reset_password.resetting_password': 'Сброс пароля...',
    'vaiu.reset_password.success_title': 'Пароль успешно сброшен',
    'vaiu.reset_password.success_description': 'Ваш пароль был успешно сброшен.',
    'vaiu.reset_password.need_help': 'Вам нужна помощь?',
    'vaiu.reset_password.request_new': 'Запросить новый ссылку для сброса пароля',
    'vaiu.continue_to_app': 'Перейти к приложению',
    'vaiu.sending': 'Отправка...',
    'vaiu.core.dashboard': 'Панель управления',
    'vaiu.core.your_balances': 'Ваши балансы',
    'vaiu.core.send_money': 'Отправить деньги',
    'vaiu.core.request_money': 'Запросить деньги',
    'vaiu.core.redeem_token': 'Использовать токен',
    'vaiu.core.add_money': 'Добавить деньги',
    'vaiu.core.view_all_transactions': 'Посмотреть все транзакции',
    'vaiu.core.referral_tokens': 'Реферални токены',
    'vaiu.core.payment_sources': 'Источники платежей',
    'vaiu.core.inbox': 'Сандуче',
    'vaiu.core.loading': 'Загрузка...',
    'vaiu.core.auth_error': 'Ошибка аутентификации:',
    'vaiu.token_slider.create_token': 'Создать токен',
    'vaiu.token_slider.to_get_money': 'Чтобы получить деньги',
    'vaiu.token_slider.to_give_money': 'Чтобы дать деньги',
    'vaiu.token_slider.give_money': 'Создать токен, чтобы дать кому-то',
    'vaiu.token_slider.request_money': 'Запросить деньги у кого-то',
    'vaiu.token_slider.create_invoice': 'Создать счет',
    'vaiu.token_slider.create_payment': 'Создать платеж',
    'vaiu.token_slider.invoice': 'Счет',
    'vaiu.token_slider.to_pay': 'Чтобы заплатить',
    'vaiu.token_form.amount_to_give': 'Вывод',
    'vaiu.token_form.amount_to_receive': 'Сумма, которую вы получите',
    'vaiu.token_form.advanced_options': 'Дополнительные опции',
    'vaiu.token_form.add_conditions': 'Добавить условия',
    'vaiu.payment.manage_accounts': 'Управление счетами',
    'vaiu.payment.funding_sources': 'Источники финансирования',
    'vaiu.payment.manage_funding_sources': 'Управление источниками финансирования',
    'vaiu.payment.add_payment_method': 'Добавить способ оплаты',
    'vaiu.payment.choose_funding_description': 'Выберите безопасный способ финансирования. Ваши данные зашифрованы.',
    'vaiu.payment.bank_account': 'Банковский счет',
    'vaiu.payment.bank_account_description': 'Подключите свой банковский счет через Plaid',
    'vaiu.payment.no_fees': 'Без комиссий',
    'vaiu.payment.most_secure': 'Наиболее безопасный',
    'vaiu.payment.ach_transfers': 'АЦХ переводы',
    'vaiu.payment.free': 'Бесплатно',
    'vaiu.payment.business_days': '1-3 рабочих дня',
    'vaiu.payment.credit_card': 'Кредитная/дебетовая карта',
    'vaiu.payment.credit_card_description': 'Оплатите картой через Stripe',
    'vaiu.payment.instant_transfers': 'Мгновенные переводы',
    'vaiu.payment.widely_accepted': 'Широко принят',
    'vaiu.payment.easy_setup': 'Простая настройка',
    'vaiu.payment.card_fee': '~2.9% + $0.30',
    'vaiu.payment.instant': 'Мгновенно',
    'vaiu.payment.paypal': 'ПаyПал',
    'vaiu.payment.choose_method': 'Выберите метод',
    'vaiu.payment.setup': 'Настройка',
    'vaiu.payment.complete': 'Завершено',
    'vaiu.payment.your_funding_sources': 'Ваши источники финансирования',
    'vaiu.payment.verified': 'Проверено',
    'vaiu.payment.toast_token_created': 'Токен Создан',
    'vaiu.payment.toast_token_created_desc': 'Токен {{key}} успешно создан',
    'vaiu.payment.toast_payment_delivered_successfully': 'Платеж Успешно Доставлен',
    'vaiu.payment.toast_payment_delivered_successfully_desc': 'Ваш платеж {{amount}} {{currency}} был доставлен {{name}}',
    'vaiu.payment.toast_payment_to_inbox': 'Платеж Отправлен во Входящие',
    'vaiu.payment.toast_payment_to_inbox_desc': '{{name}} рассмотрит и примет ваш платеж',
    'vaiu.payment.toast_recipient_not_found': 'Получатель Не Найден',
    'vaiu.payment.toast_recipient_not_found_desc': 'Получатель не найден. Токен был создан и может быть передан вручную.',
    'vaiu.payment.toast_payment_delivered': 'Платеж Доставлен',
    'vaiu.payment.toast_payment_delivered_desc': 'Ваш платеж был доставлен {{name}}',
    'confirmIdentity.title': 'Подтвердите личность',
    'confirmIdentity.prompt': 'Нам нужно проверить ваше имя перед подключением банка. Подтвердите:',
    'confirmIdentity.yourName': 'Это ваше имя?',
    'confirmIdentity.confirmButton': "Да, это я",
    'confirmIdentity.disclaimer': 'Это имя будет использоваться для проверки вашего банковского счёта нашим платёжным провайдером.',
    'confirmIdentity.requiredMessage': 'Для добавления банковского счёта требуются ваше имя и фамилия.',
    'confirmIdentity.updateProfileMessage': 'Пожалуйста, добавьте имя и фамилию в ваш профиль перед продолжением.',
    'confirmIdentity.updateProfile': 'Обновить мой профиль',
    'confirmIdentity.updateNameLink': "Неверно? Обновите имя в профиле",
    'confirmIdentity.yes': 'Да',
    'confirmIdentity.no': 'Нет',
    'vaiu.password_strength.very_weak': 'Very Weak',
    'vaiu.password_strength.weak': 'Weak',
    'vaiu.password_strength.medium': 'Medium',
    'vaiu.password_strength.strong': 'Strong',
    'vaiu.password_strength.very_strong': 'Very Strong',
  },
  pt: {
    'common.back': 'Voltar',
    'common.next': 'Próximo',
    'common.save': 'Salvar',
    'common.submit': 'Enviar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Apagar',
    'common.edit': 'Editar',
    'common.add': 'Adicionar',
    'common.remove': 'Remover',
    'common.confirm': 'Confirmar',
    'common.close': 'Fechar',
    'common.loading': 'Carregando...',
    'common.error': 'Erro',
    'common.success': 'Sucesso',
    'common.note': 'Nota',
    'common.optional': 'Opcional',
    'common.required': 'Obrigatório',
    'common.message': 'Mensagem',
    'common.recipient': 'Destinatário',
    'common.id': 'ID',
    'common.amount': 'Valor',
    'common.reference': 'Referência',
    'common.goBack': 'Voltar',
    'common.transactionId': 'ID da Transação',
    'common.currency': 'Moeda',
    'common.loadingProfile': 'Carregando perfil...',
    'common.give_money_to': 'Dar dinheiro para {{name}}',
    'common.give_money_to_stranger': 'Dar dinheiro a este estranho',
    'common.kybVerified': 'KYB Verificado',
    'common.kybPending': 'KYB Pendente',
    'common.kybRejected': 'KYB Rejeitado',
    'common.notListed': 'Não Listado',
    'common.unknown': 'Desconhecido',
    'userPublicProfile.loading': 'Carregando perfil...',
    'userPublicProfile.notFound': 'Perfil Não Encontrado',
    'userPublicProfile.notFoundDesc': 'Perfil de usuário não encontrado',
    'userPublicProfile.memberSince': 'Membro desde',
    'userPublicProfile.sendMoney': 'Enviar Dinheiro',
    'userPublicProfile.profileInformation': 'Informações do Perfil',
    'userPublicProfile.userId': 'ID do Usuário',
    'userPublicProfile.username': 'Nome de Usuário',
    'userPublicProfile.notSet': 'Não definido',
    'userPublicProfile.roles': 'Funções',
    'guest.continue_as_guest': 'Continuar como invitado',
    'guest.login_description': 'Acesso completo durante 48 horas. Inscreva-se para poder iniciar sessão',
    'guest.upgrade_account': 'Atualizar conta',
    'guest.upgrade_prompt.title': 'Atualize sua conta de convidado',
    'guest.upgrade_prompt.description': 'Salve seu histórico e desbloqueie todas as funcionalidades registrando-se.',
    'guest.upgrade_prompt.continue': 'Continuar para atualizar',
    'guest.session_indicator.title': 'Sessão convidado',
    'guest.session_indicator.time_left': 'Tempo restante',
    'guest.session_indicator.sign_up_notice': 'Inscreva-se para salvar seus dados e continuar usando o aplicativo.',
    'guest.session_indicator.session_expired': 'Sessão expirada',
    'guest.session_indicator.session_expired_message': 'Sua sessão de convidado expirou. Por favor, faça login ou registre-se para continuar.',
    'guest.session_indicator.sign_up': 'Registre-se',
    'guest.logout.warning.title': 'Sair como convidado?',
    'guest.logout.warning.description': 'Se você sair, perderá o acesso à sua conta de convidado. Todos os cartões que você adicionou e seu histórico de transações ficarão inacessíveis, a menos que você se registre para uma conta permanente.',
    'guest.logout.cancel': 'Cancelar',
    'guest.logout.confirm': 'Sair mesmo assim',
    'vaiu.sign_in': 'Entrar',
    'vaiu.sign_up': 'Registar',
    'vaiu.or': 'OU',
    'vaiu.password': 'Senha',
    'vaiu.forgot_password': 'Esqueceu a senha?',
    'vaiu.signing_in': 'Entrando no processo...',
    'vaiu.otp_login.title': 'Entrar com o número de telefone',
    'vaiu.otp_login.description': 'Digite seu número de telefone para receber um código de uso único.',
    'vaiu.otp_login.phone_placeholder': 'Seu número de telefone',
    'vaiu.otp_login.request_code': 'Solicitar código',
    'vaiu.otp_login.requesting_code': 'Solicitando código...',
    'vaiu.otp_login.enter_code_title': 'Digite o código de verificação',
    'vaiu.otp_login.enter_code_description': 'Um código foi enviado para {{phoneNumber}}',
    'vaiu.otp_login.verify': 'Verificar',
    'vaiu.otp_login.verifying': 'Verificando...',
    'vaiu.otp_login.resend_code': 'Reenviar código',
    'vaiu.otp_login.resend_code_in': 'Reenviar código em {{seconds}}s',
    'vaiu.otp_login.wrong_number': 'Número incorreto?',
    'vaiu.back': 'Voltar',
    'vaiu.email': 'Email',
    'vaiu.forgot_password.title': 'Esqueceu a senha?',
    'vaiu.forgot_password.description': 'Digite seu endereço de email e nós lhe enviaremos um link para redefinir sua senha.',
    'vaiu.forgot_password.send_reset_link': 'Enviar link de redefinição',
    'vaiu.forgot_password.remember_password': 'Lembra da senha?',
    'vaiu.forgot_password.email_sent_title': 'Verifique seu email',
    'vaiu.forgot_password.email_sent_description': 'Enviamos um link de redefinição de senha para {{email}}',
    'vaiu.forgot_password.check_spam': 'Se você não receber o email, verifique sua pasta de spam.',
    'vaiu.forgot_password.try_different_email': 'Tente outro email',
    'vaiu.back_to_login': 'Voltar para o login',
    'vaiu.new_password': 'Nova senha',
    'vaiu.confirm_password': 'Confirmar senha',
    'vaiu.reset_password.form_title': 'Redefina sua senha',
    'vaiu.reset_password.form_description': 'Digite sua nova senha abaixo',
    'vaiu.reset_password.submit': 'Redefinir senha',
    'vaiu.reset_password.resetting_password': 'Redefinindo senha...',
    'vaiu.reset_password.success_title': 'Senha redefinida com sucesso',
    'vaiu.reset_password.success_description': 'Sua senha foi redefinida com sucesso.',
    'vaiu.reset_password.need_help': 'Precisa de ajuda?',
    'vaiu.reset_password.request_new': 'Solicitar um novo link de redefinição',
    'vaiu.continue_to_app': 'Continuar para o aplicativo',
    'vaiu.sending': 'Enviando...',
    'vaiu.core.dashboard': 'Painel de controle',
    'vaiu.core.your_balances': 'Seus saldos',
    'vaiu.core.send_money': 'Enviar dinheiro',
    'vaiu.core.request_money': 'Solicitar dinheiro',
    'vaiu.core.redeem_token': 'Canjear token',
    'vaiu.core.add_money': 'Adicionar dinheiro',
    'vaiu.core.view_all_transactions': 'Ver todas as transações',
    'vaiu.core.referral_tokens': 'Tokens de referência',
    'vaiu.core.payment_sources': 'Fontes de pagamento',
    'vaiu.core.inbox': 'Caixa de entrada',
    'vaiu.core.loading': 'Carregando...',
    'vaiu.core.auth_error': 'Erro de autenticação:',
    'vaiu.token_slider.create_token': 'Criar Token',
    'vaiu.token_slider.to_get_money': 'Para Receber Dinheiro',
    'vaiu.token_slider.to_give_money': 'Para Dar Dinheiro',
    'vaiu.token_slider.give_money': 'Criar um token para dar a alguém',
    'vaiu.token_slider.request_money': 'Solicitar dinheiro de alguém',
    'vaiu.token_slider.create_invoice': 'Criar uma fatura',
    'vaiu.token_slider.create_payment': 'Criar um pagamento',
    'vaiu.token_slider.invoice': 'Fatura',
    'vaiu.token_slider.to_pay': 'Para Pagar',
    'vaiu.token_form.amount_to_give': 'Saque',
    'vaiu.token_form.amount_to_receive': 'Valor que você receberá',
    'vaiu.token_form.advanced_options': 'Opções Avançadas',
    'vaiu.token_form.add_conditions': 'Adicionar Condições',
    'vaiu.payment.manage_accounts': 'Gerenciar Contas',
    'vaiu.payment.funding_sources': 'Fontes de financiamento',
    'vaiu.payment.manage_funding_sources': 'Gerencie suas fontes de financiamento',
    'vaiu.payment.add_payment_method': 'Adicionar um método de pagamento',
    'vaiu.payment.choose_funding_description': 'Escolha como financiar suas transferências. Todos os métodos estão protegidos com criptografia bancária.',
    'vaiu.payment.bank_account': 'Conta bancária',
    'vaiu.payment.bank_account_description': 'Conecte sua conta corrente ou de poupança de forma segura',
    'vaiu.payment.no_fees': 'Sem taxas',
    'vaiu.payment.most_secure': 'Mais seguro',
    'vaiu.payment.ach_transfers': 'Transferências ACH',
    'vaiu.payment.free': 'Gratuito',
    'vaiu.payment.business_days': '1-3 dias úteis',
    'vaiu.payment.credit_card': 'Cartão de crédito ou débito',
    'vaiu.payment.credit_card_description': 'Adicione Visa, Mastercard ou outras cartas principais',
    'vaiu.payment.instant_transfers': 'Transferências instantâneas',
    'vaiu.payment.widely_accepted': 'Amplamente aceito',
    'vaiu.payment.easy_setup': 'Configuração fácil',
    'vaiu.payment.card_fee': '~2.9% + $0.30',
    'vaiu.payment.instant': 'Instantâneo',
    'vaiu.payment.paypal': 'PayPal',
    'vaiu.payment.choose_method': 'Escolher método',
    'vaiu.payment.setup': 'Configuração',
    'vaiu.payment.complete': 'Completo',
    'vaiu.payment.your_funding_sources': 'Suas fontes de financiamento',
    'vaiu.payment.verified': 'Verificado',
    'vaiu.payment.toast_token_created': 'Token Criado',
    'vaiu.payment.toast_token_created_desc': 'Token {{key}} criado com sucesso',
    'vaiu.payment.toast_payment_delivered_successfully': 'Pagamento Entregue com Sucesso',
    'vaiu.payment.toast_payment_delivered_successfully_desc': 'Seu pagamento de {{amount}} {{currency}} foi entregue a {{name}}',
    'vaiu.payment.toast_payment_to_inbox': 'Pagamento Enviado para a Caixa de Entrada',
    'vaiu.payment.toast_payment_to_inbox_desc': '{{name}} revisará e aceitará seu pagamento',
    'vaiu.payment.toast_recipient_not_found': 'Destinatário Não Encontrado',
    'vaiu.payment.toast_recipient_not_found_desc': 'O destinatário não pôde ser encontrado. O token foi criado e pode ser compartilhado manualmente.',
    'vaiu.payment.toast_payment_delivered': 'Pagamento Entregue',
    'vaiu.payment.toast_payment_delivered_desc': 'Seu pagamento foi entregue a {{name}}',
    'confirmIdentity.title': 'Confirme sua identidade',
    'confirmIdentity.prompt': 'Precisamos verificar seu nome antes de conectar seu banco. Por favor confirme:',
    'confirmIdentity.yourName': 'Este é o seu nome?',
    'confirmIdentity.confirmButton': "Sim, sou eu",
    'confirmIdentity.disclaimer': 'Este nome será usado para verificar sua conta bancária com nosso provedor de pagamentos.',
    'confirmIdentity.requiredMessage': 'Seu nome e sobrenome são obrigatórios para adicionar uma conta bancária.',
    'confirmIdentity.updateProfileMessage': 'Por favor adicione seu nome e sobrenome no seu perfil antes de continuar.',
    'confirmIdentity.updateProfile': 'Atualizar meu perfil',
    'confirmIdentity.updateNameLink': "Não está correto? Atualize seu nome no perfil",
    'confirmIdentity.yes': 'Sim',
    'confirmIdentity.no': 'Não',
    'vaiu.password_strength.very_weak': 'Muito Fraco',
    'vaiu.password_strength.weak': 'Fraco',
    'vaiu.password_strength.medium': 'Médio',
    'vaiu.password_strength.strong': 'Forte',
    'vaiu.password_strength.very_strong': 'Muito Forte',
  },
  el: {
    'common.back': 'Πίσω',
    'common.next': 'Επόμενο',
    'common.save': 'Αποθήκευση',
    'common.submit': 'Υποβολή',
    'common.cancel': 'Ακύρωση',
    'common.delete': 'Διαγραφή',
    'common.edit': 'Επεξεργασία',
    'common.add': 'Προσθήκη',
    'common.remove': 'Αφαίρεση',
    'common.confirm': 'Επιβεβαίωση',
    'common.close': 'Κλείσιμο',
    'common.loading': 'Φόρτωση...',
    'common.error': 'Σφάλμα',
    'common.success': 'Επιτυχία',
    'common.note': 'Σημείωση',
    'common.optional': 'Προαιρετικό',
    'common.required': 'Απαιτείται',
    'common.message': 'Μήνυμα',
    'common.recipient': 'Παραλήπτης',
    'common.id': 'ID',
    'common.amount': 'Ποσό',
    'common.reference': 'Αναφορά',
    'common.goBack': 'Πίσω',
    'common.transactionId': 'ID Συναλλαγής',
    'common.currency': 'Νόμισμα',
    'common.loadingProfile': 'Φόρτωση προφίλ...',
    'common.give_money_to': 'Δώστε χρήματα στο {{name}}',
    'common.give_money_to_stranger': 'Δώστε χρήματα σε αυτόν τον άγνωστο',
    'common.kybVerified': 'KYB Επαληθευμένο',
    'common.kybPending': 'KYB Σε Εκκρεμότητα',
    'common.kybRejected': 'KYB Απορρίφθηκε',
    'common.notListed': 'Δεν Είναι Καταχωρημένο',
    'common.unknown': 'Άγνωστο',
    'userPublicProfile.loading': 'Φόρτωση προφίλ...',
    'userPublicProfile.notFound': 'Το Προφίλ Δεν Βρέθηκε',
    'userPublicProfile.notFoundDesc': 'Το προφίλ χρήστη δεν βρέθηκε',
    'userPublicProfile.memberSince': 'Μέλος από',
    'userPublicProfile.sendMoney': 'Στείλε Χρήματα',
    'userPublicProfile.profileInformation': 'Πληροφορίες Προφίλ',
    'userPublicProfile.userId': 'ID Χρήστη',
    'userPublicProfile.username': 'Όνομα Χρήστη',
    'userPublicProfile.notSet': 'Δεν ορίστηκε',
    'userPublicProfile.roles': 'Ρόλοι',
    'guest.continue_as_guest': 'Συνέχεια ως επισκέπτης',
    'guest.login_description': 'Πλήρης πρόσβαση για 48 ώρες. Εγγραφείτε για να μπορείτε να συνδεθείτε',
    'guest.upgrade_account': 'Αναβάθμιση λογαριασμού',
    'guest.upgrade_prompt.title': 'Αναβαθμίστε τον λογαριασμό επισκέπτη σας',
    'guest.upgrade_prompt.description': 'Αποθηκεύστε το ιστορικό σας και ξεκλειδώστε όλες τις λειτουργίες εγγραφόμενοι.',
    'guest.upgrade_prompt.continue': 'Συνέχεια για αναβάθμιση',
    'guest.session_indicator.title': 'Συνεδρία επισκέπτη',
    'guest.session_indicator.time_left': 'Απομένων χρόνος',
    'guest.session_indicator.sign_up_notice': 'Εγγραφείτε για να αποθηκεύσετε τα δεδομένα σας και να συνεχίσετε να χρησιμοποιείτε την εφαρμογή.',
    'guest.session_indicator.session_expired': 'Η συνεδρία έληξε',
    'guest.session_indicator.session_expired_message': 'Η συνεδρία επισκέπτη σας έχει λήξει. Παρακαλώ εγγραφείτε ή συνδεθείτε για να συνεχίσετε.',
    'guest.session_indicator.sign_up': 'Εγγραφή',
    'guest.logout.warning.title': 'Αποσύνδεση ως επισκέπτης;',
    'guest.logout.warning.description': 'Αν αποσυνδεθείτε, θα χάσετε την πρόσβαση στον λογαριασμό επισκέπτη σας. Οποιεσδήποτε κάρτες έχετε προσθέσει και το ιστορικό συναλλαγών σας θα γίνουν απρόσιτα εκτός αν εγγραφείτε για έναν μόνιμο λογαριασμό.',
    'guest.logout.cancel': 'Ακύρωση',
    'guest.logout.confirm': 'Αποσύνδεση ούτως ή άλλως',
    'vaiu.sign_in': 'Σύνδεση',
    'vaiu.sign_up': 'Εγγραφή',
    'vaiu.or': 'ή',
    'vaiu.password': 'Κωδικός πρόσβασης',
    'vaiu.forgot_password': 'Ξεχάσατε τον κωδικό πρόσβασης;',
    'vaiu.signing_in': 'Σύνδεση...',
    'vaiu.otp_login.title': 'Σύνδεση με τηλέφωνο',
    'vaiu.otp_login.description': 'Εισαγάγετε τον αριθμό τηλεφώνου σας για να λάβετε έναν κωδικό μιας χρήσης.',
    'vaiu.otp_login.phone_placeholder': 'Ο αριθμός τηλεφώνου σας',
    'vaiu.otp_login.request_code': 'Αίτηση κωδικού',
    'vaiu.otp_login.requesting_code': 'Αίτηση κωδικού...',
    'vaiu.otp_login.enter_code_title': 'Εισαγάγετε τον κωδικό επαλήθευσης',
    'vaiu.otp_login.enter_code_description': 'Ένας κωδικός έχει σταλεί στο {{phoneNumber}}',
    'vaiu.otp_login.verify': 'Επαλήθευση',
    'vaiu.otp_login.verifying': 'Επαλήθευση...',
    'vaiu.otp_login.resend_code': 'Επανάληψη αποστολής κωδικού',
    'vaiu.otp_login.resend_code_in': 'Επανάληψη αποστολής κωδικού σε {{seconds}}s',
    'vaiu.otp_login.wrong_number': 'Λάθος αριθμός;',
    'vaiu.back': 'Πίσω',
    'vaiu.email': 'Email',
    'vaiu.forgot_password.title': 'Ξεχάσατε τον κωδικό πρόσβασης;',
    'vaiu.forgot_password.description': 'Εισαγάγετε τη διεύθυνση email σας και θα σας στείλουμε έναν σύνδεσμο για επαναφορά του κωδικού πρόσβασης.',
    'vaiu.forgot_password.send_reset_link': 'Αποστολή συνδέσμου επαναφοράς',
    'vaiu.forgot_password.remember_password': 'Θυμάστε τον κωδικό πρόσβασης;',
    'vaiu.forgot_password.email_sent_title': 'Ελέγξτε το email σας',
    'vaiu.forgot_password.email_sent_description': 'Έχουμε στείλει έναν σύνδεσμο επαναφοράς κωδικού πρόσβασης στο {{email}}',
    'vaiu.forgot_password.check_spam': 'Αν δεν βλέπετε το email, ελέγξτε τον φάκελο spam.',
    'vaiu.forgot_password.try_different_email': 'Δοκιμάστε διαφορετικό email',
    'vaiu.back_to_login': 'Πίσω στη σύνδεση',
    'vaiu.new_password': 'Νέος κωδικός πρόσβασης',
    'vaiu.confirm_password': 'Επιβεβαίωση κωδικού πρόσβασης',
    'vaiu.reset_password.form_title': 'Επαναφορά κωδικού πρόσβασης',
    'vaiu.reset_password.form_description': 'Εισαγάγετε τον νέο κωδικό πρόσβασης παρακάτω',
    'vaiu.reset_password.submit': 'Επαναφορά κωδικού πρόσβασης',
    'vaiu.reset_password.resetting_password': 'Επαναφορά κωδικού πρόσβασης...',
    'vaiu.reset_password.success_title': 'Επιτυχής επαναφορά κωδικού πρόσβασης',
    'vaiu.reset_password.success_description': 'Ο κωδικός πρόσβασης σας έχει επαναφερθεί επιτυχώς.',
    'vaiu.reset_password.need_help': 'Χρειάζεστε βοήθεια;',
    'vaiu.reset_password.request_new': 'Αίτηση νέου συνδέσμου επαναφοράς',
    'vaiu.continue_to_app': 'Συνέχεια στην εφαρμογή',
    'vaiu.sending': 'Αποστολή...',
    'vaiu.core.dashboard': 'Πίνακας ελέγχου',
    'vaiu.core.your_balances': 'Τα υπόλοιπά σας',
    'vaiu.core.send_money': 'Αποστολή χρημάτων',
    'vaiu.core.request_money': 'Αίτηση χρημάτων',
    'vaiu.core.redeem_token': 'Εξαργύρωση token',
    'vaiu.core.add_money': 'Προσθήκη χρημάτων',
    'vaiu.core.view_all_transactions': 'Προβολή όλων των συναλλαγών',
    'vaiu.core.referral_tokens': 'Tokens παραπομπής',
    'vaiu.core.payment_sources': 'Πηγές πληρωμής',
    'vaiu.core.inbox': 'Εισερχόμενα',
    'vaiu.core.loading': 'Φόρτωση...',
    'vaiu.core.auth_error': 'Σφάλμα πιστοποίησης:',
    'vaiu.token_slider.create_token': 'Δημιουργία Token',
    'vaiu.token_slider.to_get_money': 'Για να Λάβετε Χρήματα',
    'vaiu.token_slider.to_give_money': 'Για να Δώσετε Χρήματα',
    'vaiu.token_slider.give_money': 'Δημιουργήστε ένα token για να δώσετε σε κάποιον',
    'vaiu.token_slider.request_money': 'Ζητήστε χρήματα από κάποιον',
    'vaiu.token_slider.create_invoice': 'Δημιουργία τιμολογίου',
    'vaiu.token_slider.create_payment': 'Δημιουργία πληρωμής',
    'vaiu.token_slider.invoice': 'Τιμολόγιο',
    'vaiu.token_slider.to_pay': 'Για να Πληρώσετε',
    'vaiu.token_form.amount_to_give': 'Ανάληψη',
    'vaiu.token_form.amount_to_receive': 'Ποσό που θα λάβετε',
    'vaiu.token_form.advanced_options': 'Προχωρημένες Επιλογές',
    'vaiu.token_form.add_conditions': 'Προσθήκη Συνθηκών',
    'vaiu.payment.manage_accounts': 'Διαχείριση Λογαριασμών',
    'vaiu.payment.funding_sources': 'Πηγές χρηματοδότησης',
    'vaiu.payment.manage_funding_sources': 'Διαχείριση των πηγών χρηματοδότησης σας',
    'vaiu.payment.add_payment_method': 'Προσθήκη μεθόδου πληρωμής',
    'vaiu.payment.choose_funding_description': 'Επιλέξτε μια ασφαλή μέθοδο χρηματοδότησης. Οι πληροφορίες σας είναι κρυπτογραφημένες.',
    'vaiu.payment.bank_account': 'Τραπεζικός λογαριασμός',
    'vaiu.payment.bank_account_description': 'Συνδέστε τον τραπεζικό λογαριασμό σας μέσω Plaid',
    'vaiu.payment.no_fees': 'Χωρίς χρεώσεις',
    'vaiu.payment.most_secure': 'Πιο ασφαλές',
    'vaiu.payment.ach_transfers': 'Μεταφορές ACH',
    'vaiu.payment.free': 'Δωρεάν',
    'vaiu.payment.business_days': '1-3 εργάσιμες ημέρες',
    'vaiu.payment.credit_card': 'Πιστωτική/Χρεωστική Κάρτα',
    'vaiu.payment.credit_card_description': 'Πληρωμή με κάρτα μέσω Stripe',
    'vaiu.payment.instant_transfers': 'Άμεσες μεταφορές',
    'vaiu.payment.widely_accepted': 'Ευρέως αποδεκτό',
    'vaiu.payment.easy_setup': 'Εύκολη ρύθμιση',
    'vaiu.payment.card_fee': '~2.9% + $0.30',
    'vaiu.payment.instant': 'Άμεσο',
    'vaiu.payment.paypal': 'PayPal',
    'vaiu.payment.choose_method': 'Επιλογή μεθόδου',
    'vaiu.payment.setup': 'Ρύθμιση',
    'vaiu.payment.complete': 'Ολοκληρώθηκε',
    'vaiu.payment.your_funding_sources': 'Οι πηγές χρηματοδότησης σας',
    'vaiu.payment.verified': 'Επαληθευμένο',
    'vaiu.payment.toast_token_created': 'Διακριτικό Δημιουργήθηκε',
    'vaiu.payment.toast_token_created_desc': 'Το διακριτικό {{key}} δημιουργήθηκε με επιτυχία',
    'vaiu.payment.toast_payment_delivered_successfully': 'Η Πληρωμή Παραδόθηκε με Επιτυχία',
    'vaiu.payment.toast_payment_delivered_successfully_desc': 'Η πληρωμή σας {{amount}} {{currency}} παραδόθηκε σε {{name}}',
    'vaiu.payment.toast_payment_to_inbox': 'Η Πληρωμή Στάλθηκε στα Εισερχόμενα',
    'vaiu.payment.toast_payment_to_inbox_desc': 'Ο {{name}} θα εξετάσει και θα δεχτεί την πληρωμή σας',
    'vaiu.payment.toast_recipient_not_found': 'Ο Παραλήπτης Δεν Βρέθηκε',
    'vaiu.payment.toast_recipient_not_found_desc': 'Ο παραλήπτης δεν βρέθηκε. Το διακριτικό δημιουργήθηκε και μπορεί να κοινοποιηθεί χειροκίνητα.',
    'vaiu.payment.toast_payment_delivered': 'Η Πληρωμή Παραδόθηκε',
    'vaiu.payment.toast_payment_delivered_desc': 'Η πληρωμή σας παραδόθηκε σε {{name}}',
    'confirmIdentity.title': 'Επιβεβαιώστε την ταυτότητά σας',
    'confirmIdentity.prompt': 'Πρέπει να επαληθεύσουμε το όνομά σας πριν συνδέσουμε την τράπεζά σας. Παρακαλώ επιβεβαιώστε:',
    'confirmIdentity.yourName': 'Είναι αυτό το όνομά σας;',
    'confirmIdentity.confirmButton': "Ναι, είμαι εγώ",
    'confirmIdentity.disclaimer': 'Αυτό το όνομα θα χρησιμοποιηθεί για την επαλήθευση του τραπεζικού λογαριασμού σας με τον πάροχό μας πληρωμών.',
    'confirmIdentity.requiredMessage': 'Το όνομά και επώνυμο σας απαιτούνται για να προσθέσετε τραπεζικό λογαριασμό.',
    'confirmIdentity.updateProfileMessage': 'Παρακαλώ προσθέστε το όνομά και επώνυμο σας στο προφίλ σας πριν συνεχίσετε.',
    'confirmIdentity.updateProfile': 'Ενημέρωση προφίλ μου',
    'confirmIdentity.updateNameLink': "Δεν είναι σωστό; Ενημερώστε το όνομά σας στο προφίλ",
    'confirmIdentity.yes': 'Ναι',
    'confirmIdentity.no': 'Όχι',
    'vaiu.password_strength.very_weak': 'Πολύ Αδύναμο',
    'vaiu.password_strength.weak': 'Αδύναμο',
    'vaiu.password_strength.medium': 'Μέτριο',
    'vaiu.password_strength.strong': 'Δυνατό',
    'vaiu.password_strength.very_strong': 'Πολύ Δυνατό',
  },
  de: {
    'common.back': 'Zurück',
    'common.next': 'Weiter',
    'common.save': 'Speichern',
    'common.submit': 'Absenden',
    'common.cancel': 'Abbrechen',
    'common.delete': 'Löschen',
    'common.edit': 'Bearbeiten',
    'common.add': 'Hinzufügen',
    'common.remove': 'Entfernen',
    'common.confirm': 'Bestätigen',
    'common.close': 'Schließen',
    'common.loading': 'Laden...',
    'common.error': 'Fehler',
    'common.success': 'Erfolg',
    'common.note': 'Hinweis',
    'common.optional': 'Optional',
    'common.required': 'Erforderlich',
    'common.message': 'Nachricht',
    'common.recipient': 'Empfänger',
    'common.id': 'ID',
    'common.amount': 'Betrag',
    'common.reference': 'Referenz',
    'common.goBack': 'Zurück',
    'common.transactionId': 'Transaktions-ID',
    'common.currency': 'Währung',
    'common.loadingProfile': 'Profil wird geladen...',
    'common.give_money_to': 'Geld geben an {{name}}',
    'common.give_money_to_stranger': 'Geld an diesen Fremden geben',
    'common.kybVerified': 'KYB Verifiziert',
    'common.kybPending': 'KYB Ausstehend',
    'common.kybRejected': 'KYB Abgelehnt',
    'common.notListed': 'Nicht Gelistet',
    'common.unknown': 'Unbekannt',
    'userPublicProfile.loading': 'Profil wird geladen...',
    'userPublicProfile.notFound': 'Profil Nicht Gefunden',
    'userPublicProfile.notFoundDesc': 'Benutzerprofil nicht gefunden',
    'userPublicProfile.memberSince': 'Mitglied seit',
    'userPublicProfile.sendMoney': 'Geld Senden',
    'userPublicProfile.profileInformation': 'Profilinformationen',
    'userPublicProfile.userId': 'Benutzer-ID',
    'userPublicProfile.username': 'Benutzername',
    'userPublicProfile.notSet': 'Nicht festgelegt',
    'userPublicProfile.roles': 'Rollen',
    'guest.continue_as_guest': 'Als Gast fortfahren',
    'guest.login_description': 'Voller Zugang für 48 Stunden. Registrieren Sie sich, um sich anmelden zu können',
    'guest.upgrade_account': 'Konto upgraden',
    'guest.upgrade_prompt.title': 'Upgraden Sie Ihr Gastkonto',
    'guest.upgrade_prompt.description': 'Speichern Sie Ihren Verlauf und schalten Sie alle Funktionen frei, indem Sie sich registrieren.',
    'guest.upgrade_prompt.continue': 'Weiter zum Upgrade',
    'guest.session_indicator.title': 'Gastsitzung',
    'guest.session_indicator.time_left': 'Verbleibende Zeit',
    'guest.session_indicator.sign_up_notice': 'Registrieren Sie sich, um Ihre Daten zu speichern und die App weiter zu verwenden.',
    'guest.session_indicator.session_expired': 'Sitzung abgelaufen',
    'guest.session_indicator.session_expired_message': 'Ihre Gastsitzung ist abgelaufen. Bitte registrieren Sie sich oder melden Sie sich an, um fortzufahren.',
    'guest.session_indicator.sign_up': 'Registrieren',
    'guest.logout.warning.title': 'Als Gast abmelden?',
    'guest.logout.warning.description': 'Wenn Sie sich abmelden, verlieren Sie den Zugang zu Ihrem Gastkonto. Alle hinzugefügten Karten und Ihr Transaktionsverlauf werden unzugänglich, es sei denn, Sie registrieren sich für ein permanentes Konto.',
    'guest.logout.cancel': 'Abbrechen',
    'guest.logout.confirm': 'Trotzdem abmelden',
    'vaiu.sign_in': 'Anmelden',
    'vaiu.sign_up': 'Registrieren',
    'vaiu.or': 'oder',
    'vaiu.password': 'Passwort',
    'vaiu.forgot_password': 'Passwort vergessen?',
    'vaiu.signing_in': 'Anmeldung...',
    'vaiu.otp_login.title': 'Mit Telefon anmelden',
    'vaiu.otp_login.description': 'Geben Sie Ihre Telefonnummer ein, um einen Einmalcode zu erhalten.',
    'vaiu.otp_login.phone_placeholder': 'Ihre Telefonnummer',
    'vaiu.otp_login.request_code': 'Code anfordern',
    'vaiu.otp_login.requesting_code': 'Code wird angefordert...',
    'vaiu.otp_login.enter_code_title': 'Geben Sie den Bestätigungscode ein',
    'vaiu.otp_login.enter_code_description': 'Ein Code wurde an {{phoneNumber}} gesendet',
    'vaiu.otp_login.verify': 'Bestätigen',
    'vaiu.otp_login.verifying': 'Wird bestätigt...',
    'vaiu.otp_login.resend_code': 'Code erneut senden',
    'vaiu.otp_login.resend_code_in': 'Code erneut senden in {{seconds}}s',
    'vaiu.otp_login.wrong_number': 'Falsche Nummer?',
    'vaiu.back': 'Zurück',
    'vaiu.email': 'E-Mail',
    'vaiu.forgot_password.title': 'Passwort vergessen?',
    'vaiu.forgot_password.description': 'Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.',
    'vaiu.forgot_password.send_reset_link': 'Link zum Zurücksetzen senden',
    'vaiu.forgot_password.remember_password': 'Passwort erinnern?',
    'vaiu.forgot_password.email_sent_title': 'Überprüfen Sie Ihre E-Mail',
    'vaiu.forgot_password.email_sent_description': 'Wir haben einen Link zum Zurücksetzen des Passworts an {{email}} gesendet',
    'vaiu.forgot_password.check_spam': 'Wenn Sie die E-Mail nicht sehen, überprüfen Sie Ihren Spam-Ordner.',
    'vaiu.forgot_password.try_different_email': 'Versuchen Sie eine andere E-Mail',
    'vaiu.back_to_login': 'Zurück zur Anmeldung',
    'vaiu.new_password': 'Neues Passwort',
    'vaiu.confirm_password': 'Passwort bestätigen',
    'vaiu.reset_password.form_title': 'Setzen Sie Ihr Passwort zurück',
    'vaiu.reset_password.form_description': 'Geben Sie unten Ihr neues Passwort ein',
    'vaiu.reset_password.submit': 'Passwort zurücksetzen',
    'vaiu.reset_password.resetting_password': 'Passwort wird zurückgesetzt...',
    'vaiu.reset_password.success_title': 'Passwort erfolgreich zurückgesetzt',
    'vaiu.reset_password.success_description': 'Ihr Passwort wurde erfolgreich zurückgesetzt.',
    'vaiu.reset_password.need_help': 'Benötigen Sie Hilfe?',
    'vaiu.reset_password.request_new': 'Einen neuen Link zum Zurücksetzen anfordern',
    'vaiu.continue_to_app': 'Zur App fortfahren',
    'vaiu.sending': 'Wird gesendet...',
    'vaiu.core.dashboard': 'Dashboard',
    'vaiu.core.your_balances': 'Ihre Guthaben',
    'vaiu.core.send_money': 'Geld senden',
    'vaiu.core.request_money': 'Geld anfordern',
    'vaiu.core.redeem_token': 'Token einlösen',
    'vaiu.core.add_money': 'Geld hinzufügen',
    'vaiu.core.view_all_transactions': 'Alle Transaktionen anzeigen',
    'vaiu.core.referral_tokens': 'Empfehlungs-Tokens',
    'vaiu.core.payment_sources': 'Zahlungsquellen',
    'vaiu.core.inbox': 'Posteingang',
    'vaiu.core.loading': 'Laden...',
    'vaiu.core.auth_error': 'Authentifizierungsfehler:',
    'vaiu.token_slider.create_token': 'Token erstellen',
    'vaiu.token_slider.to_get_money': 'Um Geld zu erhalten',
    'vaiu.token_slider.to_give_money': 'Um Geld zu geben',
    'vaiu.token_slider.give_money': 'Erstellen Sie einen Token, um jemandem Geld zu geben',
    'vaiu.token_slider.request_money': 'Geld von jemandem anfordern',
    'vaiu.token_slider.create_invoice': 'Rechnung erstellen',
    'vaiu.token_slider.create_payment': 'Zahlung erstellen',
    'vaiu.token_slider.invoice': 'Rechnung',
    'vaiu.token_slider.to_pay': 'Um zu zahlen',
    'vaiu.token_form.amount_to_give': 'Auszahlung',
    'vaiu.token_form.amount_to_receive': 'Betrag, den Sie erhalten werden',
    'vaiu.token_form.advanced_options': 'Erweiterte Optionen',
    'vaiu.token_form.add_conditions': 'Bedingungen hinzufügen',
    'vaiu.payment.manage_accounts': 'Konten verwalten',
    'vaiu.payment.funding_sources': 'Finanzierungsquellen',
    'vaiu.payment.manage_funding_sources': 'Verwalten Sie Ihre Finanzierungsquellen',
    'vaiu.payment.add_payment_method': 'Zahlungsmethode hinzufügen',
    'vaiu.payment.choose_funding_description': 'Wählen Sie eine sichere Finanzierungsmethode. Ihre Informationen sind verschlüsselt.',
    'vaiu.payment.bank_account': 'Bankkonto',
    'vaiu.payment.bank_account_description': 'Verbinden Sie Ihr Bankkonto über Plaid',
    'vaiu.payment.no_fees': 'Keine Gebühren',
    'vaiu.payment.most_secure': 'Am sichersten',
    'vaiu.payment.ach_transfers': 'ACH-Überweisungen',
    'vaiu.payment.free': 'Kostenlos',
    'vaiu.payment.business_days': '1-3 Werktage',
    'vaiu.payment.credit_card': 'Kredit-/Debitkarte',
    'vaiu.payment.credit_card_description': 'Bezahlen Sie mit Karte über Stripe',
    'vaiu.payment.instant_transfers': 'Sofortige Überweisungen',
    'vaiu.payment.widely_accepted': 'Weit verbreitet',
    'vaiu.payment.easy_setup': 'Einfache Einrichtung',
    'vaiu.payment.card_fee': '~2.9% + $0.30',
    'vaiu.payment.instant': 'Sofort',
    'vaiu.payment.paypal': 'PayPal',
    'vaiu.payment.choose_method': 'Methode wählen',
    'vaiu.payment.setup': 'Einrichtung',
    'vaiu.payment.complete': 'Abgeschlossen',
    'vaiu.payment.your_funding_sources': 'Ihre Finanzierungsquellen',
    'vaiu.payment.verified': 'Verifiziert',
    'vaiu.payment.toast_token_created': 'Token Erstellt',
    'vaiu.payment.toast_token_created_desc': 'Token {{key}} erfolgreich erstellt',
    'vaiu.payment.toast_payment_delivered_successfully': 'Zahlung Erfolgreich Geliefert',
    'vaiu.payment.toast_payment_delivered_successfully_desc': 'Ihre Zahlung von {{amount}} {{currency}} wurde an {{name}} geliefert',
    'vaiu.payment.toast_payment_to_inbox': 'Zahlung an Posteingang Gesendet',
    'vaiu.payment.toast_payment_to_inbox_desc': '{{name}} wird Ihre Zahlung prüfen und akzeptieren',
    'vaiu.payment.toast_recipient_not_found': 'Empfänger Nicht Gefunden',
    'vaiu.payment.toast_recipient_not_found_desc': 'Der Empfänger konnte nicht gefunden werden. Der Token wurde erstellt und kann manuell geteilt werden.',
    'vaiu.payment.toast_payment_delivered': 'Zahlung Geliefert',
    'vaiu.payment.toast_payment_delivered_desc': 'Ihre Zahlung wurde an {{name}} geliefert',
    'confirmIdentity.title': 'Bestätigen Sie Ihre Identität',
    'confirmIdentity.prompt': 'Wir müssen Ihren Namen überprüfen, bevor wir Ihre Bank verbinden. Bitte bestätigen Sie:',
    'confirmIdentity.yourName': 'Ist das Ihr Name?',
    'confirmIdentity.confirmButton': "Ja, das bin ich",
    'confirmIdentity.disclaimer': 'Dieser Name wird zur Überprüfung Ihres Bankkontos bei unserem Zahlungsanbieter verwendet.',
    'confirmIdentity.requiredMessage': 'Vor- und Nachname sind erforderlich, um ein Bankkonto hinzuzufügen.',
    'confirmIdentity.updateProfileMessage': 'Bitte fügen Sie Vor- und Nachname in Ihrem Profil hinzu, bevor Sie fortfahren.',
    'confirmIdentity.updateProfile': 'Mein Profil aktualisieren',
    'confirmIdentity.updateNameLink': "Nicht richtig? Aktualisieren Sie Ihren Namen im Profil",
    'confirmIdentity.yes': 'Ja',
    'confirmIdentity.no': 'Nein',
    'vaiu.password_strength.very_weak': 'Sehr Schwach',
    'vaiu.password_strength.weak': 'Schwach',
    'vaiu.password_strength.medium': 'Mittel',
    'vaiu.password_strength.strong': 'Stark',
    'vaiu.password_strength.very_strong': 'Sehr Stark',
  },
  ro: {
    'common.back': 'Înapoi',
    'common.next': 'Următorul',
    'common.save': 'Salvare',
    'common.submit': 'Trimite',
    'common.cancel': 'Anulare',
    'common.delete': 'Ștergere',
    'common.edit': 'Editare',
    'common.add': 'Adăugare',
    'common.remove': 'Eliminare',
    'common.confirm': 'Confirmare',
    'common.close': 'Închidere',
    'common.loading': 'Se încarcă...',
    'common.error': 'Eroare',
    'common.success': 'Succes',
    'common.note': 'Notă',
    'common.optional': 'Opțional',
    'common.required': 'Obligatoriu',
    'common.message': 'Mesaj',
    'common.recipient': 'Destinatar',
    'common.id': 'ID',
    'common.amount': 'Sumă',
    'common.reference': 'Referință',
    'common.goBack': 'Înapoi',
    'common.transactionId': 'ID Tranzacție',
    'common.currency': 'Monedă',
    'common.loadingProfile': 'Se încarcă profilul...',
    'common.give_money_to': 'Trimite bani către {{name}}',
    'common.give_money_to_stranger': 'Trimite bani acestui străin',
    'common.kybVerified': 'KYB Verificat',
    'common.kybPending': 'KYB În Așteptare',
    'common.kybRejected': 'KYB Respins',
    'common.notListed': 'Nu Este Listat',
    'common.unknown': 'Necunoscut',
    'userPublicProfile.loading': 'Se încarcă profilul...',
    'userPublicProfile.notFound': 'Profilul Nu a Fost Găsit',
    'userPublicProfile.notFoundDesc': 'Profilul utilizatorului nu a fost găsit',
    'userPublicProfile.memberSince': 'Membru din',
    'userPublicProfile.sendMoney': 'Trimite Bani',
    'userPublicProfile.profileInformation': 'Informații Profil',
    'userPublicProfile.userId': 'ID Utilizator',
    'userPublicProfile.username': 'Nume Utilizator',
    'userPublicProfile.notSet': 'Nu este setat',
    'userPublicProfile.roles': 'Roluri',
    'guest.continue_as_guest': 'Continuă ca vizitator',
    'guest.login_description': 'Acces complet timp de 48 de ore. Înregistrează-te pentru a putea să te conectezi',
    'guest.upgrade_account': 'Actualizează contul',
    'guest.upgrade_prompt.title': 'Actualizează contul tău de vizitator',
    'guest.upgrade_prompt.description': 'Salvează istoricul tău și deblochează toate funcțiile înregistrându-te.',
    'guest.upgrade_prompt.continue': 'Continuă pentru actualizare',
    'guest.session_indicator.title': 'Sesiune vizitator',
    'guest.session_indicator.time_left': 'Timp rămas',
    'guest.session_indicator.sign_up_notice': 'Înregistrează-te pentru a-ți salva datele și a continua să folosești aplicația.',
    'guest.session_indicator.session_expired': 'Sesiunea a expirat',
    'guest.session_indicator.session_expired_message': 'Sesiunea ta de vizitator a expirat. Te rugăm să te înregistrezi sau să te conectezi pentru a continua.',
    'guest.session_indicator.sign_up': 'Înregistrare',
    'guest.logout.warning.title': 'Deconectare ca vizitator?',
    'guest.logout.warning.description': 'Dacă te deconectezi, vei pierde accesul la contul tău de vizitator. Toate cardurile pe care le-ai adăugat și istoricul tău de tranzacții vor deveni inaccesibile, cu excepția cazului în care te înregistrezi pentru un cont permanent.',
    'guest.logout.cancel': 'Anulează',
    'guest.logout.confirm': 'Deconectează-te oricum',
    'vaiu.sign_in': 'Conectare',
    'vaiu.sign_up': 'Înregistrare',
    'vaiu.or': 'sau',
    'vaiu.password': 'Parolă',
    'vaiu.forgot_password': 'Ai uitat parola?',
    'vaiu.signing_in': 'Se conectează...',
    'vaiu.otp_login.title': 'Conectează-te cu telefonul',
    'vaiu.otp_login.description': 'Introdu numărul tău de telefon pentru a primi un cod de o singură folosință.',
    'vaiu.otp_login.phone_placeholder': 'Numărul tău de telefon',
    'vaiu.otp_login.request_code': 'Solicită cod',
    'vaiu.otp_login.requesting_code': 'Se solicită cod...',
    'vaiu.otp_login.enter_code_title': 'Introdu codul de verificare',
    'vaiu.otp_login.enter_code_description': 'Un cod a fost trimis la {{phoneNumber}}',
    'vaiu.otp_login.verify': 'Verifică',
    'vaiu.otp_login.verifying': 'Se verifică...',
    'vaiu.otp_login.resend_code': 'Retrimite codul',
    'vaiu.otp_login.resend_code_in': 'Retrimite codul în {{seconds}}s',
    'vaiu.otp_login.wrong_number': 'Număr greșit?',
    'vaiu.back': 'Înapoi',
    'vaiu.email': 'Email',
    'vaiu.forgot_password.title': 'Ai uitat parola?',
    'vaiu.forgot_password.description': 'Introdu adresa ta de email și îți vom trimite un link pentru resetarea parolei.',
    'vaiu.forgot_password.send_reset_link': 'Trimite link de resetare',
    'vaiu.forgot_password.remember_password': 'Îți amintești parola?',
    'vaiu.forgot_password.email_sent_title': 'Verifică email-ul tău',
    'vaiu.forgot_password.email_sent_description': 'Am trimis un link de resetare a parolei la {{email}}',
    'vaiu.forgot_password.check_spam': 'Dacă nu vezi email-ul, verifică folderul de spam.',
    'vaiu.forgot_password.try_different_email': 'Încearcă alt email',
    'vaiu.back_to_login': 'Înapoi la conectare',
    'vaiu.new_password': 'Parolă nouă',
    'vaiu.confirm_password': 'Confirmă parola',
    'vaiu.reset_password.form_title': 'Resetează parola ta',
    'vaiu.reset_password.form_description': 'Introdu parola ta nouă mai jos',
    'vaiu.reset_password.submit': 'Resetează parola',
    'vaiu.reset_password.resetting_password': 'Se resetează parola...',
    'vaiu.reset_password.success_title': 'Parola a fost resetată cu succes',
    'vaiu.reset_password.success_description': 'Parola ta a fost resetată cu succes.',
    'vaiu.reset_password.need_help': 'Ai nevoie de ajutor?',
    'vaiu.reset_password.request_new': 'Solicită un link nou de resetare',
    'vaiu.continue_to_app': 'Continuă la aplicație',
    'vaiu.sending': 'Se trimite...',
    'vaiu.core.dashboard': 'Panou de control',
    'vaiu.core.your_balances': 'Soldurile tale',
    'vaiu.core.send_money': 'Trimite bani',
    'vaiu.core.request_money': 'Solicită bani',
    'vaiu.core.redeem_token': 'Răscumpără token',
    'vaiu.core.add_money': 'Adaugă bani',
    'vaiu.core.view_all_transactions': 'Vezi toate tranzacțiile',
    'vaiu.core.referral_tokens': 'Token-uri de recomandare',
    'vaiu.core.payment_sources': 'Surse de plată',
    'vaiu.core.inbox': 'Inbox',
    'vaiu.core.loading': 'Se încarcă...',
    'vaiu.core.auth_error': 'Eroare de autentificare:',
    'vaiu.token_slider.create_token': 'Creează Token',
    'vaiu.token_slider.to_get_money': 'Pentru a Primi Bani',
    'vaiu.token_slider.to_give_money': 'Pentru a Da Bani',
    'vaiu.token_slider.give_money': 'Creează un token pentru a da cuiva',
    'vaiu.token_slider.request_money': 'Solicită bani de la cineva',
    'vaiu.token_slider.create_invoice': 'Creează o factură',
    'vaiu.token_slider.create_payment': 'Creează o plată',
    'vaiu.token_slider.invoice': 'Factură',
    'vaiu.token_slider.to_pay': 'Pentru a Plăti',
    'vaiu.token_form.amount_to_give': 'Retragere',
    'vaiu.token_form.amount_to_receive': 'Suma pe care o vei primi',
    'vaiu.token_form.advanced_options': 'Opțiuni Avansate',
    'vaiu.token_form.add_conditions': 'Adaugă Condiții',
    'vaiu.payment.manage_accounts': 'Gestionează Conturile',
    'vaiu.payment.funding_sources': 'Surse de finanțare',
    'vaiu.payment.manage_funding_sources': 'Gestionează sursele tale de finanțare',
    'vaiu.payment.add_payment_method': 'Adaugă metodă de plată',
    'vaiu.payment.choose_funding_description': 'Alege o metodă sigură de finanțare. Informațiile tale sunt criptate.',
    'vaiu.payment.bank_account': 'Cont bancar',
    'vaiu.payment.bank_account_description': 'Conectează contul tău bancar prin Plaid',
    'vaiu.payment.no_fees': 'Fără taxe',
    'vaiu.payment.most_secure': 'Cel mai sigur',
    'vaiu.payment.ach_transfers': 'Transferuri ACH',
    'vaiu.payment.free': 'Gratuit',
    'vaiu.payment.business_days': '1-3 zile lucrătoare',
    'vaiu.payment.credit_card': 'Card de credit/debit',
    'vaiu.payment.credit_card_description': 'Plătește cu cardul prin Stripe',
    'vaiu.payment.instant_transfers': 'Transferuri instantanee',
    'vaiu.payment.widely_accepted': 'Larg acceptat',
    'vaiu.payment.easy_setup': 'Configurare ușoară',
    'vaiu.payment.card_fee': '~2.9% + $0.30',
    'vaiu.payment.instant': 'Instantanee',
    'vaiu.payment.paypal': 'PayPal',
    'vaiu.payment.choose_method': 'Alege metodă',
    'vaiu.payment.setup': 'Configurare',
    'vaiu.payment.complete': 'Completat',
    'vaiu.payment.your_funding_sources': 'Sursele tale de finanțare',
    'vaiu.payment.verified': 'Verificat',
    'vaiu.payment.toast_token_created': 'Token Creat',
    'vaiu.payment.toast_token_created_desc': 'Tokenul {{key}} a fost creat cu succes',
    'vaiu.payment.toast_payment_delivered_successfully': 'Plata Livrată cu Succes',
    'vaiu.payment.toast_payment_delivered_successfully_desc': 'Plata dvs. de {{amount}} {{currency}} a fost livrată către {{name}}',
    'vaiu.payment.toast_payment_to_inbox': 'Plata Trimisă în Inbox',
    'vaiu.payment.toast_payment_to_inbox_desc': '{{name}} va examina și accepta plata dvs.',
    'vaiu.payment.toast_recipient_not_found': 'Destinatarul Nu a Fost Găsit',
    'vaiu.payment.toast_recipient_not_found_desc': 'Destinatarul nu a putut fi găsit. Tokenul a fost creat și poate fi partajat manual.',
    'vaiu.payment.toast_payment_delivered': 'Plata Livrată',
    'vaiu.payment.toast_payment_delivered_desc': 'Plata dvs. a fost livrată către {{name}}',
    'confirmIdentity.title': 'Confirmați-vă identitatea',
    'confirmIdentity.prompt': 'Trebuie să verificăm numele dvs. înainte de a conecta banca. Vă rugăm confirmați:',
    'confirmIdentity.yourName': 'Acesta este numele dvs.?',
    'confirmIdentity.confirmButton': "Da, sunt eu",
    'confirmIdentity.disclaimer': 'Acest nume va fi folosit pentru a verifica contul bancar cu furnizorul nostru de plăți.',
    'confirmIdentity.requiredMessage': 'Prenumele și numele de familie sunt necesare pentru a adăuga un cont bancar.',
    'confirmIdentity.updateProfileMessage': 'Vă rugăm adăugați prenumele și numele de familie în profil înainte de a continua.',
    'confirmIdentity.updateProfile': 'Actualizează profilul meu',
    'confirmIdentity.updateNameLink': "Nu este corect? Actualizați numele în profil",
    'confirmIdentity.yes': 'Da',
    'confirmIdentity.no': 'Nu',
    'vaiu.password_strength.very_weak': 'Foarte Slab',
    'vaiu.password_strength.weak': 'Slab',
    'vaiu.password_strength.medium': 'Mediu',
    'vaiu.password_strength.strong': 'Puternic',
    'vaiu.password_strength.very_strong': 'Foarte Puternic',
  },
  sr: {
    'common.back': 'Назад',
    'common.next': 'Следеће',
    'common.save': 'Сачувај',
    'common.submit': 'Пошаљи',
    'common.cancel': 'Откажи',
    'common.delete': 'Избриши',
    'common.edit': 'Уреди',
    'common.add': 'Додај',
    'common.remove': 'Уклони',
    'common.confirm': 'Потврди',
    'common.close': 'Затвори',
    'common.loading': 'Учитавање...',
    'common.error': 'Грешка',
    'common.success': 'Успех',
    'common.note': 'Напомена',
    'common.optional': 'Опционално',
    'common.required': 'Обавезно',
    'common.message': 'Порука',
    'common.recipient': 'Прималац',
    'common.id': 'ID',
    'common.amount': 'Износ',
    'common.reference': 'Референца',
    'common.goBack': 'Назад',
    'common.transactionId': 'ID Трансакције',
    'common.currency': 'Валута',
    'common.loadingProfile': 'Учитавање профила...',
    'common.give_money_to': 'Дај новац {{name}}',
    'common.give_money_to_stranger': 'Дај новац овом непознатом',
    'common.kybVerified': 'KYB Проверен',
    'common.kybPending': 'KYB На Чекању',
    'common.kybRejected': 'KYB Одбијен',
    'common.notListed': 'Није На Листи',
    'common.unknown': 'Непознато',
    'userPublicProfile.loading': 'Учитавање профила...',
    'userPublicProfile.notFound': 'Профил Није Пронађен',
    'userPublicProfile.notFoundDesc': 'Профил корисника није пронађен',
    'userPublicProfile.memberSince': 'Члан од',
    'userPublicProfile.sendMoney': 'Пошаљи Новац',
    'userPublicProfile.profileInformation': 'Информације о Профилу',
    'userPublicProfile.userId': 'ID Корисника',
    'userPublicProfile.username': 'Корисничко Име',
    'userPublicProfile.notSet': 'Није постављено',
    'userPublicProfile.roles': 'Улоге',
    'guest.continue_as_guest': 'Настави као гост',
    'guest.login_description': 'Пуни приступ 48 сати. Региструјте се да бисте могли да се пријавите',
    'guest.upgrade_account': 'Надогради налог',
    'guest.upgrade_prompt.title': 'Надогради свој гостујући налог',
    'guest.upgrade_prompt.description': 'Сачувајте своју историју и откључајте све функције регистрацијом.',
    'guest.upgrade_prompt.continue': 'Настави са надоградњом',
    'guest.session_indicator.title': 'Гостујућа сесија',
    'guest.session_indicator.time_left': 'Преостало време',
    'guest.session_indicator.sign_up_notice': 'Пријавите се да бисте сачували своје податке и наставили да користите апликацију.',
    'guest.session_indicator.session_expired': 'Сесија је истекла',
    'guest.session_indicator.session_expired_message': 'Ваша гостујућа сесија је истекла. Пријавите се или региструјте да бисте наставили.',
    'guest.session_indicator.sign_up': 'Пријавите се',
    'vaiu.sign_in': 'Пријавите се',
    'vaiu.sign_up': 'Региструј се',
    'vaiu.or': 'или',
    'vaiu.password': 'Лозинка',
    'vaiu.forgot_password': 'Заборавили сте лозинку?',
    'vaiu.signing_in': 'Пријављивање...',
    'vaiu.otp_login.title': 'Пријавите се путем телефона',
    'vaiu.otp_login.description': 'Унесите свој број телефона да бисте добили једнократни код.',
    'vaiu.otp_login.phone_placeholder': 'Ваш број телефона',
    'vaiu.otp_login.request_code': 'Затражи код',
    'vaiu.otp_login.requesting_code': 'Захтевање кода...',
    'vaiu.otp_login.enter_code_title': 'Унесите верификациони код',
    'vaiu.otp_login.enter_code_description': 'Код је послат на {{phoneNumber}}',
    'vaiu.otp_login.verify': 'Верификуј',
    'vaiu.otp_login.verifying': 'Верификација...',
    'vaiu.otp_login.resend_code': 'Поново пошаљи код',
    'vaiu.otp_login.resend_code_in': 'Поново пошаљи код за {{seconds}}с',
    'vaiu.otp_login.wrong_number': 'Погрешан број?',
    'vaiu.back': 'Назад',
    'vaiu.email': 'Е-пошта',
    'vaiu.forgot_password.title': 'Заборавили сте лозинку?',
    'vaiu.forgot_password.description': 'Унесите своју е-пошту и послаћемо вам везу за ресетовање лозинке.',
    'vaiu.forgot_password.send_reset_link': 'Пошаљи везу за ресетовање',
    'vaiu.forgot_password.remember_password': 'Сећате се лозинке?',
    'vaiu.forgot_password.email_sent_title': 'Проверите своју е-пошту',
    'vaiu.forgot_password.email_sent_description': 'Послали смо везу за ресетовање лозинке на {{email}}',
    'vaiu.forgot_password.check_spam': 'Ако не видите е-пошту, проверите фасциклу за нежељену пошту.',
    'vaiu.forgot_password.try_different_email': 'Пробајте другу е-пошту',
    'vaiu.back_to_login': 'Назад на пријаву',
    'vaiu.new_password': 'Нова лозинка',
    'vaiu.confirm_password': 'Потврди лозинку',
    'vaiu.reset_password.form_title': 'Ресетујте лозинку',
    'vaiu.reset_password.form_description': 'Унесите нову лозинку испод',
    'vaiu.reset_password.submit': 'Ресетуј лозинку',
    'vaiu.reset_password.resetting_password': 'Ресетовање лозинке...',
    'vaiu.reset_password.success_title': 'Лозинка успешно ресетована',
    'vaiu.reset_password.success_description': 'Ваша лозинка је успешно ресетована.',
    'vaiu.reset_password.need_help': 'Потребна помоћ?',
    'vaiu.reset_password.request_new': 'Затражи нову везу за ресетовање',
    'vaiu.continue_to_app': 'Настави на апликацију',
    'vaiu.sending': 'Слање...',
    'vaiu.core.dashboard': 'Контролна табла',
    'vaiu.core.your_balances': 'Ваши баланси',
    'vaiu.core.send_money': 'Пошаљи новац',
    'vaiu.core.request_money': 'Затражи новац',
    'vaiu.core.redeem_token': 'Искористи токен',
    'vaiu.core.add_money': 'Додај новац',
    'vaiu.core.view_all_transactions': 'Погледај све трансакције',
    'vaiu.core.referral_tokens': 'Реферални токени',
    'vaiu.core.payment_sources': 'Извори плаћања',
    'vaiu.core.inbox': 'Пријемно сандуче',
    'vaiu.core.loading': 'Учитавање...',
    'vaiu.core.auth_error': 'Грешка аутентификације:',
    'vaiu.token_slider.create_token': 'Креирај токен',
    'vaiu.token_slider.to_get_money': 'Да примиш новац',
    'vaiu.token_slider.to_give_money': 'Да даш новац',
    'vaiu.token_slider.give_money': 'Креирај токен да даш некоме',
    'vaiu.token_slider.request_money': 'Затражи новац од некога',
    'vaiu.token_slider.create_invoice': 'Креирај фактуру',
    'vaiu.token_slider.create_payment': 'Креирај плаћање',
    'vaiu.token_slider.invoice': 'Фактура',
    'vaiu.token_slider.to_pay': 'Да платиш',
    'vaiu.token_form.amount_to_give': 'Повлачење',
    'vaiu.token_form.amount_to_receive': 'Износ који ћеш примити',
    'vaiu.token_form.advanced_options': 'Напредне опције',
    'vaiu.token_form.add_conditions': 'Додај услове',
    'vaiu.payment.manage_accounts': 'Управљај налозима',
    'vaiu.payment.funding_sources': 'Извори финансирања',
    'vaiu.payment.manage_funding_sources': 'Управљајте својим изворима финансирања',
    'vaiu.payment.add_payment_method': 'Додај начин плаћања',
    'vaiu.payment.choose_funding_description': 'Изаберите сигуран начин финансирања. Ваше информације су шифроване.',
    'vaiu.payment.bank_account': 'Банковни рачун',
    'vaiu.payment.bank_account_description': 'Повежите свој банковни рачун преко Plaid-а',
    'vaiu.payment.no_fees': 'Без накнада',
    'vaiu.payment.most_secure': 'Најсигурније',
    'vaiu.payment.ach_transfers': 'АЦХ трансфери',
    'vaiu.payment.free': 'Бесплатно',
    'vaiu.payment.business_days': '1-3 радна дана',
    'vaiu.payment.credit_card': 'Кредитна/дебитна картица',
    'vaiu.payment.credit_card_description': 'Платите картицом преко Stripe-а',
    'vaiu.payment.instant_transfers': 'Инстант трансфери',
    'vaiu.payment.widely_accepted': 'Широко прихваћено',
    'vaiu.payment.easy_setup': 'Једноставно подешавање',
    'vaiu.payment.card_fee': '~2.9% + $0.30',
    'vaiu.payment.instant': 'Инстант',
    'vaiu.payment.paypal': 'PayPal',
    'vaiu.payment.choose_method': 'Изаберите метод',
    'vaiu.payment.setup': 'Подешавање',
    'vaiu.payment.complete': 'Завршено',
    'vaiu.payment.your_funding_sources': 'Ваши извори финансирања',
    'vaiu.payment.verified': 'Верификовано',
    'vaiu.payment.toast_token_created': 'Токен Креиран',
    'vaiu.payment.toast_token_created_desc': 'Токен {{key}} је успешно креиран',
    'vaiu.payment.toast_payment_delivered_successfully': 'Плаћање Успешно Достављено',
    'vaiu.payment.toast_payment_delivered_successfully_desc': 'Ваше плаћање од {{amount}} {{currency}} је достављено {{name}}',
    'vaiu.payment.toast_payment_to_inbox': 'Плаћање Послато у Инбокс',
    'vaiu.payment.toast_payment_to_inbox_desc': '{{name}} ће прегледати и прихватити ваше плаћање',
    'vaiu.payment.toast_recipient_not_found': 'Прималац Није Пронађен',
    'vaiu.payment.toast_recipient_not_found_desc': 'Прималац није пронађен. Токен је креиран и може се поделити ручно.',
    'vaiu.payment.toast_payment_delivered': 'Плаћање Достављено',
    'vaiu.payment.toast_payment_delivered_desc': 'Ваше плаћање је достављено {{name}}',
    'confirmIdentity.title': 'Потврдите свој идентитет',
    'confirmIdentity.prompt': 'Морамо да верификујемо ваше име пре повезивања банке. Молимо потврдите:',
    'confirmIdentity.yourName': 'Да ли је ово ваше име?',
    'confirmIdentity.confirmButton': "Да, то сам ја",
    'confirmIdentity.disclaimer': 'Ово име ће се користити за верификацију вашег банковног рачуна код нашег провајдера плаћања.',
    'confirmIdentity.requiredMessage': 'Име и презиме су обавезни за додавање банковног рачуна.',
    'confirmIdentity.updateProfileMessage': 'Молимо додајте име и презиме у ваш профил пре наставка.',
    'confirmIdentity.updateProfile': 'Ажурирај мој профил',
    'confirmIdentity.updateNameLink': "Није тачно? Ажурирајте име у профилу",
    'confirmIdentity.yes': 'Да',
    'confirmIdentity.no': 'Не',
    'vaiu.password_strength.very_weak': 'Веома слабо',
    'vaiu.password_strength.weak': 'Слабо',
    'vaiu.password_strength.medium': 'Средње',
    'vaiu.password_strength.strong': 'Јако',
    'vaiu.password_strength.very_strong': 'Веома јако',
  },
  pl: {
    'common.back': 'Wstecz',
    'common.next': 'Dalej',
    'common.save': 'Zapisz',
    'common.submit': 'Wyślij',
    'common.cancel': 'Anuluj',
    'common.delete': 'Usuń',
    'common.edit': 'Edytuj',
    'common.add': 'Dodaj',
    'common.remove': 'Usuń',
    'common.confirm': 'Potwierdź',
    'common.close': 'Zamknij',
    'common.loading': 'Ładowanie...',
    'common.error': 'Błąd',
    'common.success': 'Sukces',
    'common.note': 'Uwaga',
    'common.optional': 'Opcjonalne',
    'common.required': 'Wymagane',
    'common.message': 'Wiadomość',
    'common.recipient': 'Odbiorca',
    'common.id': 'ID',
    'common.amount': 'Kwota',
    'common.reference': 'Referencja',
    'common.goBack': 'Wstecz',
    'common.transactionId': 'ID Transakcji',
    'common.currency': 'Waluta',
    'common.loadingProfile': 'Ładowanie profilu...',
    'common.give_money_to': 'Daj pieniądze {{name}}',
    'common.give_money_to_stranger': 'Daj pieniądze temu nieznajomemu',
    'common.kybVerified': 'KYB Zweryfikowany',
    'common.kybPending': 'KYB Oczekujący',
    'common.kybRejected': 'KYB Odrzucony',
    'common.notListed': 'Nie Na Liście',
    'common.unknown': 'Nieznane',
    'userPublicProfile.loading': 'Ładowanie profilu...',
    'userPublicProfile.notFound': 'Profil Nie Znaleziony',
    'userPublicProfile.notFoundDesc': 'Profil użytkownika nie został znaleziony',
    'userPublicProfile.memberSince': 'Członek od',
    'userPublicProfile.sendMoney': 'Wyślij Pieniądze',
    'userPublicProfile.profileInformation': 'Informacje o Profilu',
    'userPublicProfile.userId': 'ID Użytkownika',
    'userPublicProfile.username': 'Nazwa Użytkownika',
    'userPublicProfile.notSet': 'Nie ustawiono',
    'userPublicProfile.roles': 'Role',
    'guest.continue_as_guest': 'Kontynuuj jako gość',
    'guest.login_description': 'Pełny dostęp na 48 godzin. Zarejestruj się, aby móc się zalogować',
    'guest.upgrade_account': 'Ulepsz konto',
    'guest.upgrade_prompt.title': 'Ulepsz swoje konto gościa',
    'guest.upgrade_prompt.description': 'Zapisz swoją historię i odblokuj wszystkie funkcje, rejestrując się.',
    'guest.upgrade_prompt.continue': 'Kontynuuj ulepszanie',
    'guest.session_indicator.title': 'Sesja gościa',
    'guest.session_indicator.time_left': 'Pozostały czas',
    'guest.session_indicator.sign_up_notice': 'Zarejestruj się, aby zapisać swoje dane i kontynuować korzystanie z aplikacji.',
    'guest.session_indicator.session_expired': 'Sesja wygasła',
    'guest.session_indicator.session_expired_message': 'Twoja sesja gościa wygasła. Zarejestruj się lub zaloguj, aby kontynuować.',
    'guest.session_indicator.sign_up': 'Zarejestruj się',
    'guest.logout.warning.title': 'Wylogować się jako gość?',
    'guest.logout.warning.description': 'Jeśli się wylogujesz, stracisz dostęp do swojego konta gościa. Wszystkie dodane karty i historia transakcji staną się niedostępne, chyba że zarejestrujesz się na stałe konto.',
    'guest.logout.cancel': 'Anuluj',
    'guest.logout.confirm': 'Wyloguj się mimo to',
    'vaiu.sign_in': 'Zaloguj się',
    'vaiu.sign_up': 'Zarejestruj się',
    'vaiu.or': 'lub',
    'vaiu.password': 'Hasło',
    'vaiu.forgot_password': 'Zapomniałeś hasła?',
    'vaiu.signing_in': 'Logowanie...',
    'vaiu.otp_login.title': 'Zaloguj się przez telefon',
    'vaiu.otp_login.description': 'Wprowadź swój numer telefonu, aby otrzymać jednorazowy kod.',
    'vaiu.otp_login.phone_placeholder': 'Twój numer telefonu',
    'vaiu.otp_login.request_code': 'Poproś o kod',
    'vaiu.otp_login.requesting_code': 'Żądanie kodu...',
    'vaiu.otp_login.enter_code_title': 'Wprowadź kod weryfikacyjny',
    'vaiu.otp_login.enter_code_description': 'Kod został wysłany na {{phoneNumber}}',
    'vaiu.otp_login.verify': 'Zweryfikuj',
    'vaiu.otp_login.verifying': 'Weryfikacja...',
    'vaiu.otp_login.resend_code': 'Wyślij kod ponownie',
    'vaiu.otp_login.resend_code_in': 'Wyślij kod ponownie za {{seconds}}s',
    'vaiu.otp_login.wrong_number': 'Zły numer?',
    'vaiu.back': 'Wstecz',
    'vaiu.email': 'E-mail',
    'vaiu.forgot_password.title': 'Zapomniałeś hasła?',
    'vaiu.forgot_password.description': 'Wprowadź swój adres e-mail, a wyślemy Ci link do resetowania hasła.',
    'vaiu.forgot_password.send_reset_link': 'Wyślij link resetujący',
    'vaiu.forgot_password.remember_password': 'Pamiętasz hasło?',
    'vaiu.forgot_password.email_sent_title': 'Sprawdź swoją skrzynkę e-mail',
    'vaiu.forgot_password.email_sent_description': 'Wysłaliśmy link do resetowania hasła na {{email}}',
    'vaiu.forgot_password.check_spam': 'Jeśli nie widzisz e-maila, sprawdź folder ze spamem.',
    'vaiu.forgot_password.try_different_email': 'Spróbuj innego e-maila',
    'vaiu.back_to_login': 'Powrót do logowania',
    'vaiu.new_password': 'Nowe hasło',
    'vaiu.confirm_password': 'Potwierdź hasło',
    'vaiu.reset_password.form_title': 'Resetuj hasło',
    'vaiu.reset_password.form_description': 'Wprowadź swoje nowe hasło poniżej',
    'vaiu.reset_password.submit': 'Resetuj hasło',
    'vaiu.reset_password.resetting_password': 'Resetowanie hasła...',
    'vaiu.reset_password.success_title': 'Hasło zostało zresetowane pomyślnie',
    'vaiu.reset_password.success_description': 'Twoje hasło zostało pomyślnie zresetowane.',
    'vaiu.reset_password.need_help': 'Potrzebujesz pomocy?',
    'vaiu.reset_password.request_new': 'Poproś o nowy link resetujący',
    'vaiu.continue_to_app': 'Przejdź do aplikacji',
    'vaiu.sending': 'Wysyłanie...',
    'vaiu.core.dashboard': 'Panel',
    'vaiu.core.your_balances': 'Twoje salda',
    'vaiu.core.send_money': 'Wyślij pieniądze',
    'vaiu.core.request_money': 'Poproś o pieniądze',
    'vaiu.core.redeem_token': 'Wykorzystaj token',
    'vaiu.core.add_money': 'Dodaj pieniądze',
    'vaiu.core.view_all_transactions': 'Zobacz wszystkie transakcje',
    'vaiu.core.referral_tokens': 'Tokeny polecające',
    'vaiu.core.payment_sources': 'Źródła płatności',
    'vaiu.core.inbox': 'Skrzynka odbiorcza',
    'vaiu.core.loading': 'Ładowanie...',
    'vaiu.core.auth_error': 'Błąd uwierzytelniania:',
    'vaiu.token_slider.create_token': 'Utwórz token',
    'vaiu.token_slider.to_get_money': 'Aby otrzymać pieniądze',
    'vaiu.token_slider.to_give_money': 'Aby dać pieniądze',
    'vaiu.token_slider.give_money': 'Utwórz token, aby dać komuś',
    'vaiu.token_slider.request_money': 'Poproś kogoś o pieniądze',
    'vaiu.token_slider.create_invoice': 'Utwórz fakturę',
    'vaiu.token_slider.create_payment': 'Utwórz płatność',
    'vaiu.token_slider.invoice': 'Faktura',
    'vaiu.token_slider.to_pay': 'Aby zapłacić',
    'vaiu.token_form.amount_to_give': 'Wypłata',
    'vaiu.token_form.amount_to_receive': 'Kwota, którą otrzymasz',
    'vaiu.token_form.advanced_options': 'Opcje zaawansowane',
    'vaiu.token_form.add_conditions': 'Dodaj warunki',
    'vaiu.payment.manage_accounts': 'Zarządzaj kontami',
    'vaiu.payment.funding_sources': 'Źródła finansowania',
    'vaiu.payment.manage_funding_sources': 'Zarządzaj swoimi źródłami finansowania',
    'vaiu.payment.add_payment_method': 'Dodaj metodę płatności',
    'vaiu.payment.choose_funding_description': 'Wybierz bezpieczną metodę finansowania. Twoje informacje są szyfrowane.',
    'vaiu.payment.bank_account': 'Konto bankowe',
    'vaiu.payment.bank_account_description': 'Połącz swoje konto bankowe przez Plaid',
    'vaiu.payment.no_fees': 'Bez opłat',
    'vaiu.payment.most_secure': 'Najbezpieczniejsze',
    'vaiu.payment.ach_transfers': 'Przelewy ACH',
    'vaiu.payment.free': 'Darmowe',
    'vaiu.payment.business_days': '1-3 dni robocze',
    'vaiu.payment.credit_card': 'Karta kredytowa/debetowa',
    'vaiu.payment.credit_card_description': 'Zapłać kartą przez Stripe',
    'vaiu.payment.instant_transfers': 'Natychmiastowe przelewy',
    'vaiu.payment.widely_accepted': 'Powszechnie akceptowane',
    'vaiu.payment.easy_setup': 'Łatwa konfiguracja',
    'vaiu.payment.card_fee': '~2,9% + $0,30',
    'vaiu.payment.instant': 'Natychmiastowe',
    'vaiu.payment.paypal': 'PayPal',
    'vaiu.payment.choose_method': 'Wybierz metodę',
    'vaiu.payment.setup': 'Konfiguracja',
    'vaiu.payment.complete': 'Zakończono',
    'vaiu.payment.your_funding_sources': 'Twoje źródła finansowania',
    'vaiu.payment.verified': 'Zweryfikowane',
    'vaiu.payment.toast_token_created': 'Token Utworzony',
    'vaiu.payment.toast_token_created_desc': 'Token {{key}} został pomyślnie utworzony',
    'vaiu.payment.toast_payment_delivered_successfully': 'Płatność Dostarczona Pomyślnie',
    'vaiu.payment.toast_payment_delivered_successfully_desc': 'Twoja płatność {{amount}} {{currency}} została dostarczona do {{name}}',
    'vaiu.payment.toast_payment_to_inbox': 'Płatność Wysłana do Skrzynki',
    'vaiu.payment.toast_payment_to_inbox_desc': '{{name}} przejrzy i zaakceptuje Twoją płatność',
    'vaiu.payment.toast_recipient_not_found': 'Odbiorca Nie Znaleziony',
    'vaiu.payment.toast_recipient_not_found_desc': 'Odbiorca nie został znaleziony. Token został utworzony i może być udostępniony ręcznie.',
    'vaiu.payment.toast_payment_delivered': 'Płatność Dostarczona',
    'vaiu.payment.toast_payment_delivered_desc': 'Twoja płatność została dostarczona do {{name}}',
    'confirmIdentity.title': 'Potwierdź swoją tożsamość',
    'confirmIdentity.prompt': 'Musimy zweryfikować Twoje imię przed podłączeniem banku. Proszę potwierdzić:',
    'confirmIdentity.yourName': 'Czy to jest Twoje imię?',
    'confirmIdentity.confirmButton': "Tak, to ja",
    'confirmIdentity.disclaimer': 'To imię zostanie wykorzystane do weryfikacji konta bankowego u naszego dostawcy płatności.',
    'confirmIdentity.requiredMessage': 'Imię i nazwisko są wymagane do dodania konta bankowego.',
    'confirmIdentity.updateProfileMessage': 'Proszę dodać imię i nazwisko w swoim profilu przed kontynuowaniem.',
    'confirmIdentity.updateProfile': 'Zaktualizuj mój profil',
    'confirmIdentity.updateNameLink': "Nieprawidłowe? Zaktualizuj imię w profilu",
    'confirmIdentity.yes': 'Tak',
    'confirmIdentity.no': 'Nie',
    'vaiu.password_strength.very_weak': 'Bardzo słabe',
    'vaiu.password_strength.weak': 'Słabe',
    'vaiu.password_strength.medium': 'Średnie',
    'vaiu.password_strength.strong': 'Silne',
    'vaiu.password_strength.very_strong': 'Bardzo silne',
  },
};

const sharedTranslations = translations;
const paymentTranslationMaps = paymentTranslations as Record<string, Record<string, string>>;
const englishFallback = {
  ...sharedTranslations.en,
  ...componentTranslations.en,
  ...(paymentTranslationMaps.en || {}),
};

// Merge shared translations with component translations and fall back to English for any gaps.
const allTranslations = {
  en: englishFallback,
  fr: { ...englishFallback, ...sharedTranslations.fr, ...componentTranslations.fr, ...(paymentTranslationMaps.fr || {}) },
  es: { ...englishFallback, ...sharedTranslations.es, ...componentTranslations.es, ...(paymentTranslationMaps.es || {}) },
  sq: { ...englishFallback, ...sharedTranslations.sq, ...componentTranslations.sq, ...(paymentTranslationMaps.sq || {}) },
  ru: { ...englishFallback, ...sharedTranslations.ru, ...componentTranslations.ru, ...(paymentTranslationMaps.ru || {}) },
  pt: { ...englishFallback, ...sharedTranslations.pt, ...componentTranslations.pt, ...(paymentTranslationMaps.pt || {}) },
  el: { ...englishFallback, ...sharedTranslations.el, ...componentTranslations.el, ...(paymentTranslationMaps.el || {}) },
  de: { ...englishFallback, ...sharedTranslations.de, ...componentTranslations.de, ...(paymentTranslationMaps.de || {}) },
  ro: { ...englishFallback, ...sharedTranslations.ro, ...componentTranslations.ro, ...(paymentTranslationMaps.ro || {}) },
  sr: { ...englishFallback, ...sharedTranslations.sr, ...componentTranslations.sr, ...(paymentTranslationMaps.sr || {}) },
  pl: { ...englishFallback, ...sharedTranslations.pl, ...componentTranslations.pl, ...(paymentTranslationMaps.pl || {}) },
};

class I18n {
  private currentLanguage: SupportedLanguage;
  
  constructor() {
    this.currentLanguage = (localStorage.getItem('vaiu_language') || 'en') as SupportedLanguage;
  }
  
  getLanguage(): SupportedLanguage {
    return this.currentLanguage;
  }
  
  setLanguage(lang: SupportedLanguage): void {
    this.currentLanguage = lang;
    localStorage.setItem('vaiu_language', lang);
    
    // Dispatch custom event for components to listen to
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: lang }));
  }

  __(key: string, params?: Record<string, string | number>): string {
    const translations = allTranslations[this.currentLanguage] || allTranslations.en;
    let translation = translations[key] || key;

    if (params) {
      Object.keys(params).forEach((param) => {
        const value = String(params[param]);
        translation = translation.replace(new RegExp(`\\{\\{${param}\\}\\}`, 'g'), value);
        translation = translation.replace(new RegExp(`\\{${param}\\}`, 'g'), value);
      });
    }

    return translation;
  }
}

export const i18n = new I18n();
export default i18n; 
